import type { ScormSavedProgress } from './scorm/cmi';
import { api, ApiError, httpBlob, retryTransient } from './api';
import { attemptPhotoErrorText, learnerPhotoSizeError } from './upload-limits';

// ── Mirror of backend wiki.service.ts DTOs ─────────────────────────

export interface WikiBlockDto {
  id: string;
  code: string;
  name: string;
  icon: string | null;
  color: string;
  sortOrder: number;
  articleCount: number;
}

export interface WikiCategoryDto {
  id: string;
  slug: string;
  name: string;
  parentId: string | null;
  blockCode: string;
  sortOrder: number;
  articleCount: number;
  /** Роли (role.code), которым видна категория. Пусто/undefined = видно всем. */
  visibleToRoles?: string[];
}

export interface WikiArticleListItem {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  status: 'DRAFT' | 'REVIEW' | 'PUBLISHED' | 'ARCHIVED';
  blocks: string[];
  categoryName: string | null;
  type: string;
  publishedAt: string | null;
  isRead: boolean;
  requiresAck: boolean;
  isAcked: boolean;
  /** Обложка (URL) и иконка-эмодзи — карточки и hero (фаза 0.1). */
  coverUrl?: string | null;
  icon?: string | null;
  /** Время чтения, мин (считает бэк, ≈180 слов/мин). */
  readingMinutes?: number | null;
  /** Порядок среди сиблингов (для drag-сортировки карточек). Меньше = выше. */
  sortOrder: number;
  /** Сниппет совпадения по телу статьи (~150 символов, без markdown).
   *  Приходит только в поисковой выдаче; null/undefined — показывать excerpt. */
  snippet?: string | null;
}

export interface WikiArticleDetailDto extends WikiArticleListItem {
  bodyHtml: string;
  /**
   * id файлов (Диск и вложения), на которые ссылается текст, но их уже нет — удалены с Диска (лежат в Корзине)
   * или вложение удалено. Читалка получает bodyHtml с плашкой «Файл удалён», редактор помечает карточку.
   */
  deletedFileIds?: string[];
  bodyMd: string;
  parentArticleId: string | null;
  breadcrumb: Array<{ id: string; slug: string; title: string }>;
  attachments: Array<{
    id: string;
    kind: string;
    fileName: string;
    mimeType: string;
    fileSize: number;
    sortOrder: number;
    isInline: boolean;
  }>;
  backlinks: Array<{ id: string; slug: string; title: string }>;
  tags: Array<{ code: string; name: string; color: string }>;
  related?: Array<{ slug: string; title: string }>;
  linkedKpiCode?: string | null;
  downloadable?: boolean;
  /** Дата вступления редакции в силу (ISO) — для поля «Действует с» в редакторе. */
  effectiveFrom?: string | null;
  /** Поля для безопасного passthrough при инлайн-правке (frontmatter
   *  заменяется целиком — без них роли/уведомления/wizard затёрлись бы). */
  visibleToRoles?: string[];
  notifyOnUpdate?: boolean;
  wizardOrder?: number | null;
  expectedDays?: number | null;
  /** Владелец и проверка актуальности (фаза 0.5). */
  ownerId?: string | null;
  /** Человек или «Отдел: <название>» — готовая подпись. */
  ownerName?: string | null;
  /** Владелец-отдел (код из «Поддержка партнёров → Отделы») и его название; у статьи либо человек, либо отдел. */
  ownerDeptCode?: string | null;
  ownerDeptLabel?: string | null;
  /** Зритель — руководитель или участник отдела-владельца (может подтвердить актуальность). */
  viewerInOwnerDept?: boolean;
  verifyEveryDays?: number | null;
  verifiedAt?: string | null;
  verifiedByName?: string | null;
  templateCode?: string | null;
  needsVerify?: boolean;
  /** Сколько раз открывали — карточка «Актуальность» в правом рейле. */
  viewCount?: number;
  /** В избранном у текущего посетителя (фаза 0.6). */
  isFavorite?: boolean;
  /** Отложенная публикация (ISO), фаза 1.1. */
  publishAt?: string | null;
  /** id CRM-новости рассылки (фаза 1.3). */
  newsPostId?: string | null;
  /** Slug статьи-замены у архивной статьи (фаза 3.4). */
  replacedBySlug?: string | null;
  /** Рецензент, пока статья на проверке (фаза 3.5). */
  reviewerId?: string | null;
  reviewerName?: string | null;
  reviewRequestedAt?: string | null;
  /** История изменений: существенные правки с «Что изменилось». */
  changelog?: Array<{ version: number; summary: string; at: string; authorName: string | null }>;
  /** Ознакомился раньше, но вышла существенная версия — нужен повторный ack. */
  needsReAck?: boolean;
  /** Имя автора-создателя (для мета-строки). null если не задан. */
  author?: string | null;
  /** Текущий номер версии (для «Версия X.0» в мете). null/0 → не показываем. */
  version?: number | null;
}

/** Публичный элемент рейла «Популярные» (топ по реальным просмотрам). */
export interface PopularArticleDto {
  id: string;
  slug: string;
  title: string;
  viewCount: number;
  blockCode: string | null;
  categoryName: string | null;
}

export interface RecommendedDto {
  id: string;
  slug: string;
  title: string;
  icon: string | null;
  excerpt: string | null;
  categoryName: string | null;
  reason: string;
  reasonKind: 'ack' | 'role' | 'popular' | 'new';
}

export interface ArticlesResponse {
  items: WikiArticleListItem[];
  total: number;
  /** «Возможно, вы имели в виду» при 0 результатов (фаза 4.1). */
  didYouMean?: string | null;
  /** Слова, добавленные из словаря синонимов. */
  expandedWith?: string[];
  /** true — результат ранжирован гибридно (FTS + эмбеддинги). */
  hybrid?: boolean;
}

/** Сгруппированный inline-поиск (решение #6): Статьи / Файлы / Курсы / Стандарты. */
export interface GroupedSearchDto {
  query: string;
  articles: WikiArticleListItem[];
  standards: WikiArticleListItem[];
  files: Array<{
    id: string;
    fileName: string;
    kind: string;
    articleSlug: string | null;
    articleTitle: string | null;
  }>;
  courses: Array<{ id: string; name: string; description: string | null }>;
  totalArticles: number;
}

export interface WikiCommentDto {
  id: string;
  bodyMd: string;
  isResolved: boolean;
  createdAt: string;
  author: { id: string; name: string; avatarUrl: string | null };
  canManage: boolean;
  replies: WikiCommentDto[];
}

// ── API methods ────────────────────────────────────────────────────

export interface BroadcastTargetsDto {
  categories: Array<{ id: string; name: string; requiresModeration: boolean }>;
  partners: Array<{ id: string; name: string }>;
  cities: Array<{ city: string; partnerCount: number }>;
  employeeRoles: Array<{ code: string; name: string }>;
  tradingPoints: Array<{ id: string; label: string }>;
}
export interface BroadcastInput {
  categoryId: string;
  priority?: 'NORMAL' | 'IMPORTANT' | 'REQUIRES_ACTION';
  deadline?: string | null;
  forRoles?: string[];
  forPartnerIds?: string[];
  forCities?: string[];
  forEmployees?: boolean;
  employeeRoleCodes?: string[];
  employeeTradingPointIds?: string[];
  broadcastTelegram?: boolean;
  broadcastMax?: boolean;
  fullBody?: boolean;
}

export interface ReactionsDto {
  counts: Record<string, number>;
  mine: string[];
}

export interface WikiFavoriteItemDto {
  id: string;
  slug: string;
  title: string;
  icon: string | null;
  coverUrl: string | null;
  readingMinutes: number | null;
  categoryName: string | null;
  type: string;
  addedAt: string;
}

export interface WikiTemplateDto {
  code: string;
  name: string;
  description: string;
  icon: string;
  type: string;
  verifyEveryDays: number | null;
  requiresAck: boolean;
}

export const wikiApi = {
  /** Список 3+ блоков с counts. */
  listBlocks: () => api.get<WikiBlockDto[]>('/wiki/blocks'),

  /** Категории (плоский список, дерево строит фронт через parentId). */
  listCategories: (blockCode?: string) => {
    const q = blockCode ? `?block=${encodeURIComponent(blockCode)}` : '';
    return api.get<WikiCategoryDto[]>(`/wiki/categories${q}`);
  },

  /** Все теги каталога (read-only) — для выбора в редакторе. */
  listTags: () => api.get<Array<{ code: string; name: string; color: string }>>('/wiki/tags'),
  /** Шаблоны новой статьи (фаза 0.5). */
  listTemplates: () => api.get<WikiTemplateDto[]>('/wiki/templates'),
  /** Избранное (фаза 0.6). */
  listFavorites: () => api.get<WikiFavoriteItemDto[]>('/wiki/favorites'),
  setFavorite: (slug: string, on: boolean) =>
    on
      ? api.post<{ isFavorite: boolean }>(`/wiki/articles/${encodeURIComponent(slug)}/favorite`)
      : api.delete<{ isFavorite: boolean }>(`/wiki/articles/${encodeURIComponent(slug)}/favorite`),
  /** Клик по результату поиска — fire-and-forget (метрика успешности поиска). */
  searchClick: (query: string, articleId: string): void => {
    const body = JSON.stringify({ query, articleId });
    try {
      if (navigator.sendBeacon) {
        navigator.sendBeacon('/api/wiki/search/click', new Blob([body], { type: 'application/json' }));
        return;
      }
    } catch { /* fallback ниже */ }
    void api.post('/wiki/search/click', { query, articleId }).catch(() => undefined);
  },
  /** Счётчик «Мои задачи» для бейджа (фаза 1.5). */
  tasksCount: () => api.get<{ standardsPending: number; courses: number; total: number }>('/wiki/tasks/count'),
  /** Подсказка для @упоминаний (фаза 1.4). */
  suggestUsers: (q: string) => api.get<Array<{ id: string; fullName: string }>>(`/wiki/users/suggest?q=${encodeURIComponent(q)}`),
  /** Реакции (фаза 1.2). */
  getReactions: (targetType: 'article' | 'news', targetId: string) =>
    api.get<ReactionsDto>(`/wiki/reactions?targetType=${targetType}&targetId=${encodeURIComponent(targetId)}`),
  toggleReaction: (targetType: 'article' | 'news', targetId: string, emoji: string) =>
    api.post<ReactionsDto>('/wiki/reactions', { targetType, targetId, emoji }),
  /** PDF статьи (скачивание). */
  articlePdfUrl: (slug: string) => `/api/wiki/articles/${encodeURIComponent(slug)}/pdf`,

  /** Список статей с фильтрами + пагинация + поиск через FTS. */
  listArticles: (params: {
    blockCode?: string;
    categoryId?: string;
    search?: string;
    status?: 'PUBLISHED' | 'DRAFT' | 'REVIEW' | 'ARCHIVED' | 'ALL';
    limit?: number;
    offset?: number;
    /** 'order' — сортировка по sortOrder (для drag-карточек); дефолт — publishedAt desc. */
    sort?: 'recent' | 'order';
    /** Фильтры поиска (фаза 4.2). */
    type?: string;
    audienceRole?: string;
    verifiedOnly?: boolean;
    publishedFrom?: string;
    publishedTo?: string;
    /** Гибридный ранкинг (FTS + эмбеддинги) — страница поиска. */
    hybrid?: boolean;
  }) => {
    const q = new URLSearchParams();
    if (params.blockCode) q.set('blockCode', params.blockCode);
    if (params.categoryId) q.set('categoryId', params.categoryId);
    if (params.search) q.set('search', params.search);
    if (params.status) q.set('status', params.status);
    if (params.limit) q.set('limit', String(params.limit));
    if (params.offset) q.set('offset', String(params.offset));
    if (params.sort) q.set('sort', params.sort);
    if (params.type) q.set('type', params.type);
    if (params.audienceRole) q.set('audienceRole', params.audienceRole);
    if (params.verifiedOnly) q.set('verifiedOnly', '1');
    if (params.publishedFrom) q.set('publishedFrom', params.publishedFrom);
    if (params.publishedTo) q.set('publishedTo', params.publishedTo);
    if (params.hybrid) q.set('hybrid', '1');
    const s = q.toString();
    return api.get<ArticlesResponse>(`/wiki/articles${s ? `?${s}` : ''}`);
  },

  /** «Рекомендовано» (фаза 4.3): персональная подборка для главной. */
  recommended: (limit = 8) => api.get<RecommendedDto[]>(`/wiki/articles/recommended?limit=${limit}`),
  /** Autocomplete для Cmd+K — top-8 + total, с логированием запроса. */
  searchSuggest: (q: string) =>
    api.get<ArticlesResponse>(`/wiki/search/suggest?q=${encodeURIComponent(q)}`),

  /** Сгруппированный inline-поиск: Статьи / Файлы / Курсы / Стандарты (решение #6). */
  searchGrouped: (q: string) =>
    api.get<GroupedSearchDto>(`/wiki/search/grouped?q=${encodeURIComponent(q)}`),

  /** Комментарии статьи (дерево top-level + replies). */
  listComments: (slug: string) =>
    api.get<WikiCommentDto[]>(`/wiki/articles/${encodeURIComponent(slug)}/comments`),
  addComment: (slug: string, bodyMd: string, parentId?: string) =>
    api.post<WikiCommentDto>(`/wiki/articles/${encodeURIComponent(slug)}/comments`, { bodyMd, parentId }),
  resolveComment: (id: string, resolved: boolean) =>
    api.post<{ ok: true }>(`/wiki/comments/${id}/resolve`, { resolved }),
  deleteComment: (id: string) => api.delete<{ ok: true }>(`/wiki/comments/${id}`),

  /** Предложить правку статьи (Q84). */
  suggestEdit: (slug: string, body: { anchor: string; from: string; to: string; comment?: string }) =>
    api.post<{ id: string }>(`/wiki/articles/${encodeURIComponent(slug)}/suggest-edit`, body),

  /** Топ «Популярные» по реальным просмотрам (с ACL вьюера). */
  popularArticles: (limit = 5) =>
    api.get<PopularArticleDto[]>(`/wiki/articles/popular?limit=${limit}`),

  /** Полная статья по slug + backlinks + attachments + tags. */
  getArticle: (slug: string) =>
    api.get<WikiArticleDetailDto>(`/wiki/articles/${encodeURIComponent(slug)}`),

  /** Дерево страниц блока (для Layout sidebar, Teamly-style). */
  getPagesTree: (blockCode: string) =>
    api.get<PageTreeNode[]>(
      `/wiki/pages-tree?block=${encodeURIComponent(blockCode)}`,
    ),

  /** Граф знаний (Q40): узлы статей + рёбра backlinks. */
  getGraph: () => api.get<WikiGraphDto>('/wiki/graph'),
};

export interface WikiGraphDto {
  nodes: Array<{ id: string; slug: string; title: string; blockCode: string | null }>;
  edges: Array<{ source: string; target: string }>;
}

export interface PageTreeNode {
  id: string;
  slug: string;
  title: string;
  type: string;
  sortOrder: number;
  children: PageTreeNode[];
}

// ── Opening (wizard открытия точки) ─────────────────────────────────

export interface OpeningStepDto {
  idx: number;
  slug: string;
  title: string;
  wizardOrder: number;
  expectedDays: number | null;
  doneCount: number;
  totalCount: number;
  status: 'completed' | 'in_progress' | 'waiting' | 'overdue';
  items: Array<{ key: string; isDone: boolean; doneAt: string | null }>;
}

export interface MyOpeningProgressDto {
  partnerId: string;
  startedAt: string;
  proposedOpenDate: string | null;
  confirmedOpenDate: string | null;
  completedAt: string | null;
  ukNotes: string | null;
  totalSteps: number;
  completedSteps: number;
  currentStepIndex: number;
  currentStepTitle: string;
  currentStepSlug: string | null;
  progressPercent: number;
  overdueCount: number;
  daysLeft: number | null;
  steps: OpeningStepDto[];
}

export interface MyOpeningResponse {
  progress: MyOpeningProgressDto | null;
  hasPartner: boolean;
}

export interface RoomInspectionAnswer {
  key: string;
  label: string;
  value: string;
  points: number;
  isCritical?: boolean;
}
export interface RoomInspectionInput {
  address: string;
  listingUrl?: string | null;
  verdict: string;
  commercialPercent: number;
  investmentScore: number;
  techFails?: string[];
  commercial?: RoomInspectionAnswer[];
  investment?: RoomInspectionAnswer[];
  tech?: RoomInspectionAnswer[];
  comment?: string | null;
  photos?: Array<{ name: string; dataUrl: string }>;
}

export const openingApi = {
  /** Мой прогресс открытия (для франчайзи-партнёра). */
  getMine: () => api.get<MyOpeningResponse>('/wiki/opening/me'),

  /** Полная заявка осмотра (что заполнял партнёр) — для просмотра/догрузки. */
  getRoomInspection: (id: string) =>
    api.get<Record<string, unknown> & { id: string; status: string; stageCode: string | null; adminComment: string | null }>(
      `/wiki/opening/room-inspection/${encodeURIComponent(id)}`,
    ),

  /** Повторная отправка: догрузить фото / сменить адрес, ссылку, комментарий. */
  updateRoomInspection: (
    id: string,
    body: { address?: string; listingUrl?: string | null; comment?: string | null; photos?: Array<{ name: string; dataUrl: string }> },
  ) =>
    api.patch<{ ok: true; photosUploaded: number }>(
      `/wiki/opening/room-inspection/${encodeURIComponent(id)}`,
      body,
    ),

  /** Мои заявки «Осмотр помещения» — статус + решение УК. */
  myRoomInspections: () =>
    api.get<
      Array<{
        id: string;
        address: string;
        verdict: string;
        status: string;
        stageCode: string | null;
        adminComment: string | null;
        createdAt: string;
      }>
    >('/wiki/opening/my-room-inspections'),

  /** Каталог критериев «Анализ помещения» — единый источник (бэк). */
  roomCriteria: () => api.get<import('./roomCatalog').RoomCatalog>('/wiki/opening/room-criteria'),

  // ── Админка каталога критериев (kb:admin) ──
  adminRoomCriteria: () =>
    api.get<{ config: import('./roomCatalog').RoomCatalog; version: number | null }>(
      '/wiki/opening/admin/room-criteria',
    ),
  adminRoomCriteriaVersions: () =>
    api.get<Array<{ version: number; note: string | null; isActive: boolean; createdAt: string }>>(
      '/wiki/opening/admin/room-criteria/versions',
    ),
  saveRoomCriteria: (config: import('./roomCatalog').RoomCatalog, note?: string) =>
    api.post<{ version: number }>('/wiki/opening/admin/room-criteria', { config, note }),
  restoreRoomCriteria: (version: number) =>
    api.post<{ version: number }>(`/wiki/opening/admin/room-criteria/restore/${version}`, {}),

  /** Ориентир аренды по кластерам населения (реальные данные сети). */
  rentBenchmark: () =>
    api.get<{
      rentPercent: number;
      clusters: Array<{
        key: string;
        label: string;
        pointsCount: number;
        medianRevenue: number | null;
        recommendedRent: number | null;
      }>;
    }>('/wiki/opening/rent-benchmark'),

  /** Доступные торговые точки для селектора в шапке. */
  tradingPoints: () =>
    api.get<{
      canSeeAll: boolean;
      points: Array<{ id: string; name: string; city: string | null; timezone: string }>;
    }>('/wiki/opening/trading-points'),

  /** Стартовать открытие. */
  start: (proposedOpenDate?: string) =>
    api.post<{ ok: true }>('/wiki/opening/me', { proposedOpenDate }),

  /** Партнёр меняет предложенную дату. */
  proposeDate: (proposedOpenDate: string) =>
    api.patch<{ ok: true }>('/wiki/opening/me/date', { proposedOpenDate }),

  /** Отправить заявку «Осмотр помещения» куратору → CRM «Заявки партнёров». */
  submitRoomInspection: (payload: RoomInspectionInput) =>
    api.post<{ ok: true; submissionId: string }>(
      '/wiki/opening/room-inspection',
      payload,
    ),

  /** Отметить пункт чек-листа. */
  toggleItem: (
    articleSlug: string,
    itemKey: string,
    isDone: boolean,
    note?: string,
  ) =>
    api.patch<{ ok: true }>(
      `/wiki/opening/me/items/${encodeURIComponent(articleSlug)}/${encodeURIComponent(itemKey)}`,
      { isDone, note },
    ),

  /** Список всех открытий для дашборда УК. */
  dashboard: () =>
    api.get<
      Array<{
        partnerId: string;
        partnerName: string;
        cityName: string | null;
        startedAt: string;
        proposedOpenDate: string | null;
        confirmedOpenDate: string | null;
        completedSteps: number;
        totalSteps: number;
        progressPercent: number;
        daysLeft: number | null;
        daysSinceProgress: number;
        isOverdue: boolean;
      }>
    >('/wiki/opening/dashboard'),

  /** УК подтверждает дату. */
  confirmDate: (
    partnerId: string,
    confirmedOpenDate?: string,
    ukNotes?: string,
  ) =>
    api.post<{ ok: true }>(
      `/wiki/opening/${encodeURIComponent(partnerId)}/confirm-date`,
      { confirmedOpenDate, ukNotes },
    ),

  /** Завершить открытие вручную. */
  complete: (partnerId: string) =>
    api.post<{ ok: true }>(
      `/wiki/opening/${encodeURIComponent(partnerId)}/complete`,
      {},
    ),

  // ── Блок A: договор аренды ──
  /** Авто-поля для предзаполнения формы договора (из партнёра/заявки). */
  leaseContractPrefill: (id: string) =>
    api.get<LeaseContractPrefill>(
      `/wiki/opening/room-inspection/${encodeURIComponent(id)}/lease-contract/prefill`,
    ),

  // ── Блоки B/C: чертёж + согласование проекта ──
  /** Снимок цикла заявки: стадия, чертёж, проект. */
  openingLifecycle: (id: string) =>
    api.get<OpeningLifecycle>(
      `/wiki/opening/room-inspection/${encodeURIComponent(id)}/lifecycle`,
    ),

  /** Блок B: приложить чертёж/план (файл base64 и/или ссылка RemPlanner). */
  attachProjectPlan: (
    id: string,
    body: { file?: { name: string; dataUrl: string } | null; link?: string | null },
  ) =>
    api.post<{ ok: true; plan: ProjectPlanItem[] }>(
      `/wiki/opening/room-inspection/${encodeURIComponent(id)}/project-plan`,
      body,
    ),

  /** Блок C (партнёр): согласовать / вернуть проект с замечаниями. */
  reviewProject: (id: string, body: { action: 'approve' | 'changes'; comment?: string | null }) =>
    api.post<{ ok: true; stageCode: string }>(
      `/wiki/opening/room-inspection/${encodeURIComponent(id)}/review-project`,
      body,
    ),

  /** Сгенерировать и скачать .docx договора аренды (единый шаблон). Возвращает Blob. */
  generateLeaseContract: (id: string, body: LeaseContractFields) =>
    httpBlob(
      `/wiki/opening/room-inspection/${encodeURIComponent(id)}/lease-contract`,
      body,
    ),
};

/** Поля договора аренды (Блок A) — те же ключи, что у бэкенда. */
export interface LeaseContractFields {
  // авто-реквизиты арендатора (наш партнёр)
  арендатор_наименование?: string;
  арендатор_директор?: string;
  арендатор_инн?: string;
  арендатор_кпп?: string;
  арендатор_огрн?: string;
  арендатор_юр_адрес?: string;
  арендатор_телефон?: string;
  арендатор_email?: string;
  арендатор_рс?: string;
  арендатор_банк?: string;
  арендатор_кс?: string;
  арендатор_бик?: string;
  договор_франшизы_номер?: string;
  город?: string;
  адрес_помещения?: string;
  площадь_м2?: string;
  // вводит партнёр
  номер_договора?: string;
  дата?: string;
  арендодатель_наименование?: string;
  основание_собственности?: string;
  срок_аренды_лет?: string;
  льготных_месяцев?: string;
  ставка_льготная?: string;
  ставка_основная?: string;
  обеспечительный_платёж?: string;
  мощность_квт?: string;
}

/** Авто-реквизиты арендатора (наш партнёр) + адрес/площадь из заявки. */
export interface LeaseContractPrefill {
  арендатор_наименование: string;
  арендатор_директор: string;
  арендатор_инн: string;
  арендатор_кпп: string;
  арендатор_огрн: string;
  арендатор_юр_адрес: string;
  арендатор_телефон: string;
  арендатор_email: string;
  арендатор_рс: string;
  арендатор_банк: string;
  арендатор_кс: string;
  арендатор_бик: string;
  договор_франшизы_номер: string;
  город: string;
  адрес_помещения: string;
  площадь_м2: string;
}

/** Чертёж/план замеров (Блок B). */
export interface ProjectPlanItem {
  kind: 'file' | 'link';
  name: string;
  url: string | null;
  path: string | null;
  uploadedAt: string;
  /** Новые файлы (с 28.10.2026): лежат в хранилище CRM, на Google Диск уходят в фоне. */
  fileId?: string;
  storage?: 'crm' | string;
  /** Ссылка на Google Диске — появляется после выгрузки. */
  driveUrl?: string | null;
}

/** Проект помещения от УК (Блок C). */
export interface ProjectInfo {
  fileUrl: string | null;
  fileName?: string | null;
  uploadedByUk?: boolean;
  status: 'review' | 'approved' | 'changes_requested';
  partnerComment?: string | null;
  finalPlanUrl?: string | null;
  finalPlanName?: string | null;
  /** Новые файлы (с 28.10.2026): `storage: 'crm'`, ссылки на Google Диск — после выгрузки. */
  storage?: 'crm' | string;
  driveUrl?: string | null;
  finalPlanDriveUrl?: string | null;
  uploadedAt?: string | null;
  reviewedAt?: string | null;
}

/** Снимок жизненного цикла «Заявки на помещение» (Блоки B/C). */
export interface OpeningLifecycle {
  id: string;
  status: string;
  stageCode: string | null;
  address: string;
  approved: boolean;
  projectPlan: ProjectPlanItem[];
  project: ProjectInfo | null;
}

// ── Editor (создание / редактирование статей) ──────────────────────

export interface ArticleFrontmatterInput {
  slug?: string;
  blocks?: string[];
  category?: string;
  tags?: string[];
  // Типы материала FISHKA HUB: article/instruction/checklist/regulation/faq
  // (чипы редактора = фильтры лендинга). Остальные — wizard/спец-рендеры.
  type?:
    | 'article'
    | 'instruction'
    | 'checklist'
    | 'regulation'
    | 'faq'
    | 'standard'
    | 'wizard_step'
    | 'decision_tree'
    | 'document_template'
    | 'publication';
  excerpt?: string;
  /** Обложка (URL вложения) и иконка-эмодзи. */
  cover?: string;
  icon?: string;
  /** Владелец — человек ЛИБО отдел (null — снять; выбранный отдел вытесняет человека), срок проверки
   *  (целое 1…1095, null = не проверять), код шаблона (фаза 0.5). */
  owner_id?: string | null;
  owner_dept?: string | null;
  verify_every_days?: number | null;
  template?: string;
  /** Отложенная публикация (ISO). '' — снять расписание. */
  publish_at?: string;
  wizard_order?: number;
  expected_days?: number;
  effective_from?: string;
  downloadable?: boolean;
  notify_on_update?: boolean;
  requires_ack?: boolean;
  visible_to_roles?: string[];
  linked_kpi_code?: string;
  status?: 'DRAFT' | 'REVIEW' | 'PUBLISHED' | 'ARCHIVED';
}

export interface AiClassifyDto {
  blocks: string[];
  category: string | null;
  tags: string[];
  excerpt: string;
  suggestedTitle: string;
  confidence: number;
  reasoning: string;
}

/** Версия статьи/стандарта. label — semver (v{N}.0), effectiveFrom — «действует с». */
export interface WikiVersionDto {
  version: number;
  label: string;
  title: string;
  changeNote: string | null;
  /** «Что изменилось» (фаза 3.5). */
  summary?: string | null;
  effectiveFrom: string | null;
  authorName: string | null;
  createdAt: string;
}

/** Один элемент структурного diff (ADD/DEL/MOD). */
export interface WikiDiffChange {
  type: 'ADD' | 'DEL' | 'MOD';
  text: string;
  textOld?: string;
  line: number;
}

/** Ответ /versions/:v/diff — сырые тела (обратная совместимость) + структурный diff. */
export interface WikiVersionDiffDto {
  versionBody: string;
  currentBody: string;
  versionTitle: string;
  currentTitle: string;
  label: string;
  changes: WikiDiffChange[];
  summary: { added: number; deleted: number; modified: number };
}

export const editorApi = {
  /** Создать статью. Если parentArticleId задан — подстраница. */
  create: (args: {
    title: string;
    bodyMd: string;
    frontmatter?: ArticleFrontmatterInput;
    parentArticleId?: string;
    /** Код шаблона (GET /wiki/templates) — тело/тип/срок проверки по умолчанию. */
    templateCode?: string;
  }) =>
    api.post<{ id: string; slug: string; vaultPath: string }>(
      '/wiki/articles',
      args,
    ),

  /** «Подтвердить актуальность» — без новой версии (владелец или kb:edit_any). */
  verify: (articleId: string) =>
    api.post<{ id: string; slug: string; verifiedAt: string }>(
      `/wiki/articles/${encodeURIComponent(articleId)}/verify`,
    ),

  /** «Разослать» (фаза 1.3): справочники таргетинга, рассылка, отвязка. */
  broadcastTargets: () => api.get<BroadcastTargetsDto>('/wiki/broadcast/targets'),
  broadcast: (articleId: string, input: BroadcastInput) =>
    api.post<{ newsPostId: string; status: string }>(`/wiki/articles/${encodeURIComponent(articleId)}/broadcast`, input),
  unlinkBroadcast: (articleId: string) =>
    api.delete<{ ok: true }>(`/wiki/articles/${encodeURIComponent(articleId)}/broadcast`),
  /** Поиск пользователей для поля «Владелец» (id + имя). */
  searchUsers: (search: string) =>
    api.get<Array<{ id: string; fullName: string }>>(
      `/wiki/editor/users?search=${encodeURIComponent(search)}`,
    ),
  /** Отделы УК для поля «Владелец»: код, название, сколько в отделе действующих людей. */
  ownerDepartments: () =>
    api.get<Array<{ code: string; label: string; peopleCount: number }>>('/wiki/editor/departments'),

  /** Переместить страницу в дереве (drag-n-drop в sidebar). */
  movePage: (articleId: string, parentArticleId: string | null, sortOrder: number) =>
    api.patch<{ ok: true }>(
      `/wiki/articles/${encodeURIComponent(articleId)}/move`,
      { parentArticleId, sortOrder },
    ),

  /**
   * Bulk-reorder карточек статей в разделе (drag прямо на карточках).
   * ids — в новом порядке; бэк проставит sortOrder=0,10,20,… не трогая parent.
   */
  /** Перенос статьи в другой подраздел + порядок целевой группы. */
  moveArticle: (articleId: string, categoryId: string | null, orderedIds: string[]) =>
    api.patch<{ ok: true }>('/wiki/articles/move', { articleId, categoryId, orderedIds }),

  reorderArticles: (ids: string[]) =>
    api.patch<{ ok: true; updated: number }>('/wiki/articles/reorder', { ids }),

  /** История версий статьи (Q45) + версионирование стандартов (label/effectiveFrom). */
  listVersions: (articleId: string) =>
    api.get<WikiVersionDto[]>(
      `/wiki/articles/${encodeURIComponent(articleId)}/versions`,
    ),
  versionDiff: (articleId: string, version: number) =>
    api.get<WikiVersionDiffDto>(
      `/wiki/articles/${encodeURIComponent(articleId)}/versions/${version}/diff`,
    ),
  rollback: (articleId: string, version: number) =>
    api.post<{ id: string; slug: string }>(
      `/wiki/articles/${encodeURIComponent(articleId)}/versions/${version}/rollback`,
    ),

  /** Инбокс предложенных правок (Q84, УК). */
  listSuggestedEdits: () =>
    api.get<Array<{ id: string; articleSlug: string; articleTitle: string; from: string; to: string; comment: string | null; proposerName: string; createdAt: string }>>(
      '/wiki/suggested-edits',
    ),
  reviewSuggestedEdit: (id: string, action: 'accept' | 'reject', note?: string) =>
    api.post<{ status: string; applied?: boolean }>(
      `/wiki/suggested-edits/${id}/review`,
      { action, note },
    ),

  /** Обновить статью по id. baseVersion — optimistic locking: версия, от
   *  которой начата правка; при несовпадении с текущей бэк вернёт 409
   *  (конфликт одновременной правки). Не передан — last-write-wins. */
  update: (
    id: string,
    args: {
      title: string;
      bodyMd: string;
      frontmatter?: ArticleFrontmatterInput;
      baseVersion?: number;
      /** «Что изменилось» (фаза 3.5): обязательно при правке опубликованного стандарта с requires_ack. */
      summary?: string;
    },
  ) =>
    api.patch<{ id: string; slug: string; vaultPath: string; diskTrashed?: TrashedDiskFile[] }>(
      `/wiki/articles/${encodeURIComponent(id)}`,
      args,
    ),

  /** Архивировать статью; replacedBySlug — slug статьи-замены (баннер по прямой ссылке). */
  archive: (id: string, replacedBySlug?: string | null) =>
    api.delete<{ ok: true }>(
      `/wiki/articles/${encodeURIComponent(id)}${replacedBySlug ? `?replacedBy=${encodeURIComponent(replacedBySlug)}` : ''}`,
    ),

  /** Архив (Q99): список архивированных статей. */
  listArchived: () =>
    api.get<Array<{ id: string; slug: string; title: string; updatedAt: string; blocks: string[] }>>(
      '/wiki/archived',
    ),
  /** Восстановить статью из архива → PUBLISHED + пересоздать .md. */
  restoreArticle: (id: string) =>
    api.post<{ id: string; slug: string }>(`/wiki/articles/${encodeURIComponent(id)}/restore`),

  /** Удалить архивную статью НАВСЕГДА (необратимо). Только для status=ARCHIVED. */
  purgeArticle: (id: string) =>
    api.delete<{ ok: true }>(`/wiki/articles/${encodeURIComponent(id)}/purge`),

  /** Очередь ревью (Q44/Q81): статьи с низкой AI-уверенностью (REVIEW). */
  listReviewQueue: () =>
    api.get<
      Array<{
        id: string;
        slug: string;
        title: string;
        excerpt: string | null;
        vaultPath: string;
        updatedAt: string;
        blocks: string[];
        tags: string[];
        authorName: string | null;
        reviewerId: string | null;
        reviewerName: string | null;
        reviewRequestedAt: string | null;
      }>
    >('/wiki/review-queue'),
  /** Опубликовать статью из очереди ревью (kb:publish). */
  publishArticle: (id: string) =>
    api.post<{ id: string; slug: string }>(`/wiki/articles/${encodeURIComponent(id)}/publish`),
  /** Workflow с рецензентом (фаза 3.5). */
  requestReview: (id: string, reviewerId: string) =>
    api.post<{ id: string; slug: string; status: 'REVIEW' }>(`/wiki/articles/${encodeURIComponent(id)}/request-review`, { reviewerId }),
  approveArticle: (id: string) =>
    api.post<{ id: string; slug: string }>(`/wiki/articles/${encodeURIComponent(id)}/approve`),
  returnArticle: (id: string, note?: string) =>
    api.post<{ id: string; slug: string; status: 'DRAFT' }>(`/wiki/articles/${encodeURIComponent(id)}/return`, { note: note ?? '' }),

  /** Спросить AI: какие blocks/category/tags/excerpt подойдут к черновику. */
  aiClassify: (args: { title: string; bodyMd: string }) =>
    api.post<AiClassifyDto | { error: string }>('/wiki/ai/classify', args),
};

// ── AI-ассистент (FISHKA HUB) ───────────────────────────────────────

/** Найденный материал (статья) в ответе AI-ассистента. */
export interface AiMaterialRef {
  slug: string;
  title: string;
}

/** Ответ AI на свободный вопрос (RAG поверх FTS базы знаний). */
export interface AiAnswerDto {
  answer: string;
  materials: AiMaterialRef[];
  /** true — ответ построен LLM; false — деградация до выдачи материалов. */
  aiUsed: boolean;
}

/** Краткий пересказ статьи. */
export interface AiSummaryDto {
  summary: string;
  slug: string;
  title: string;
  aiUsed: boolean;
}

/** Draft-теста по статье (формат вопроса совместим с lmsAdminApi.addQuestion). */
export interface AiTestDraftDto {
  questions: Array<{ text: string; options: Array<{ text: string; isCorrect: boolean }> }>;
  slug: string;
  title: string;
  aiUsed: boolean;
  /** Взято N статей (usedArticles) из найденных по источникам (totalArticles). */
  usedArticles?: number;
  totalArticles?: number;
}

/** Источники AI-вопросов: разделы/подразделы/страницы с подстраницами и статьи. */
export interface AiTestSourcesDto {
  sections: Array<{ key: string; kind: 'block' | 'category' | 'page'; title: string; path: string | null; articles: number }>;
  articles: Array<{ key: string; title: string; path: string | null }>;
}

/**
 * AI-ассистент: ask (вопрос→ответ+материалы), summarize (пересказ статьи),
 * generateTest (draft-тест по статье — для конструктора курсов).
 */
export interface AiRewriteDto {
  slug: string;
  title: string;
  originalMd: string;
  rewrittenMd: string;
  changes: WikiDiffChange[];
  summary: { added: number; deleted: number; modified: number };
  aiUsed: boolean;
  note: string | null;
}
/** «Дубли статей»: сигналы, по которым пара попала в проверку (wiki-duplicates.ts на бэке). */
export interface AiDuplicateSignalsDto {
  /** Имена общих файлов. */
  sameFile?: string[];
  /** Похожесть текста 0..1. */
  text?: number;
  /** Похожесть заголовков 0..1. */
  title?: number;
  /** Близость по смыслу 0..1 (только если есть векторы). */
  semantic?: number;
  sameCategory?: boolean;
}
export type AiDuplicateVerdictDto = 'same' | 'overlap' | 'different';
export interface AiDuplicateSideDto {
  id: string;
  slug: string;
  title: string;
  /** PUBLISHED | REVIEW | DRAFT. */
  status: string;
  category: string | null;
}
export interface AiDuplicatePairDto {
  a: AiDuplicateSideDto;
  b: AiDuplicateSideDto;
  /** Общий балл 0..1. */
  similarity: number;
  signals: AiDuplicateSignalsDto;
  /** Вердикт ИИ; null — не проверял. */
  verdict: AiDuplicateVerdictDto | null;
  aiDuplicate: boolean | null;
  aiReason: string | null;
}
export interface AiDuplicatesDto {
  pairs: AiDuplicatePairDto[];
  aiUsed: boolean;
  /** Сколько пар проверил ИИ (до 15). */
  aiChecked?: number;
  /** Сколько статей участвовало в поиске. */
  articles?: number;
}
export interface AiStaleCheckDto {
  overdue: VerifyQueueRow[];
  conflicts: Array<{ a: { slug: string; title: string }; b: { slug: string; title: string }; category: string; details: string }>;
  checkedPairs: number;
  aiUsed: boolean;
}

export type AiEditJobDto =
  | { status: 'running'; startedAt: string; progress?: unknown }
  | { status: 'done'; startedAt: string; result: AiRewriteDto }
  /** code 'lost' — сервер перезапускался и потерял задачу: запрос можно повторить. */
  | { status: 'error'; startedAt: string; error: string; code?: string };

export interface AiSettingsDto {
  settings: { provider: 'gigachat' | 'anthropic'; proxyUrl: string | null; model: string; source: 'db' | 'env'; updatedAt: string | null };
  usage: { month: string; rows: Array<{ model: string; calls: number; inputTokens: number; outputTokens: number }> };
  /** Есть ключ у ИИ, выбранного в общей настройке CRM (Claude — ANTHROPIC_API_KEY, OpenAI — OPENAI_API_KEY). */
  claudeKeyConfigured: boolean;
  /** Общий ИИ CRM («Настройки → Интеграции → ИИ»): Claude или OpenAI. */
  aiProvider?: 'anthropic' | 'openai';
}

export interface AiArticleVariantDto {
  title: string;
  type: 'instruction' | 'checklist' | 'regulation' | 'standard' | 'article' | 'faq';
  category: string | null;
  categoryIsNew: boolean;
  excerpt: string;
  approach: string;
  bodyMd: string;
}
export interface AiArticleVariantsDto { variants: AiArticleVariantDto[]; aiUsed: boolean; note: string | null }
export type AiJobDto<T> =
  | { status: 'running'; startedAt: string; progress?: unknown }
  | { status: 'done'; startedAt: string; result: T }
  | { status: 'error'; startedAt: string; error: string; code?: string };

export interface AiPingDto { ok: boolean; ms: number; models: string[]; error: string | null; proxy: boolean; provider?: 'anthropic' | 'openai'; model?: string | null }

export const aiApi = {
  /** Переписать по стайлгайду (фаза 4.4) — diff-превью, применение кнопкой. */
  rewrite: (slug: string) => api.post<AiRewriteDto>('/wiki/ai/rewrite', { slug }),
  /** «ИИ: править статью» — фоновая задача (ответ бывает дольше минуты). */
  // Запуск и опрос задач ИИ переживают краткий сбой сервера (retryTransient).
  startEdit: (body: { slug: string; bodyMd?: string; instruction?: string }) => retryTransient(() => api.post<{ jobId: string }>('/wiki/ai/edit', body)),
  editStatus: (jobId: string) => retryTransient(() => api.get<AiEditJobDto>(`/wiki/ai/edit/${encodeURIComponent(jobId)}`)),
  /** «Новая статья → ИИ: из текста» — 2–4 варианта статьи (фоновая задача). */
  startArticleVariants: (body: { blockCode: string; text: string; title?: string; category?: string; type?: string; count?: number }) =>
    retryTransient(() => api.post<{ jobId: string }>('/wiki/ai/article-variants', body)),
  /** То же с файлом (PDF/DOCX/TXT до 30 МБ, multipart): ИИ читает документ и коротко описывает его. */
  startArticleVariantsFile: (fd: FormData) =>
    retryTransient(async () => {
      const r = await fetch('/api/wiki/ai/article-variants/file', { method: 'POST', credentials: 'include', body: fd });
      if (!r.ok) {
        const b = (await r.json().catch(() => null)) as { message?: string | string[] } | null;
        const msg = Array.isArray(b?.message) ? b.message.join('; ') : b?.message;
        throw new ApiError(r.status, b, msg || `HTTP ${r.status}`);
      }
      return (await r.json()) as { jobId: string };
    }),
  articleVariantsStatus: (jobId: string) => retryTransient(() => api.get<AiJobDto<AiArticleVariantsDto>>(`/wiki/ai/edit/${encodeURIComponent(jobId)}`)),
  /** «Администрирование → ИИ» (kb:admin). */
  settings: () => api.get<AiSettingsDto>('/wiki/ai/settings'),
  saveSettings: (body: { provider?: 'gigachat' | 'anthropic'; proxyUrl?: string; model?: string }) => api.patch<AiSettingsDto['settings']>('/wiki/ai/settings', body),
  testConnection: () => api.post<AiPingDto>('/wiki/ai/settings/test'),
  duplicates: () => api.get<AiDuplicatesDto>('/wiki/ai/duplicates'),
  staleCheck: () => api.get<AiStaleCheckDto>('/wiki/ai/stale-check'),
  /** Свободный вопрос к базе знаний — RAG-ответ + найденные материалы. */
  ask: (query: string) => api.post<AiAnswerDto>('/wiki/ai/ask', { query }),
  /** Краткий пересказ статьи по slug (контракт — fileId; у нас slug). */
  summarize: (slug: string) => api.post<AiSummaryDto>('/wiki/ai/summarize', { slug }),
  /** Сгенерировать draft-тест по статье (kb:create|kb:admin). */
  generateTest: (slug: string) => api.post<AiTestDraftDto>('/wiki/ai/generate-test', { slug }),
  /** Вопросы по нескольким источникам («block:…», «category:…», «page:…», «article:…»), count — 5/10/15/20. */
  generateTestFrom: (sources: string[], count: number) => api.post<AiTestDraftDto>('/wiki/ai/generate-test', { sources, count }),
  testSources: (q: string) => api.get<AiTestSourcesDto>(`/wiki/ai/test-sources?q=${encodeURIComponent(q)}`),
};

export interface AdminBlock { id: string; code: string; name: string; icon: string | null; color: string; sortOrder: number; isHidden: boolean; visibleToRoles: string[]; writeRoles: string[]; articleCount: number; }
export interface AdminTag { id: string; code: string; name: string; color: string; articleCount: number; }

/** Период дашборда аналитики (тоггл Неделя/Месяц/Квартал). */
export type AnalyticsPeriod = 'week' | 'month' | 'quarter';

/** Админ-CRUD блоков и тегов (kb:admin). */
/** Строка очереди «Актуальность» (фаза 3.4). */
export interface VerifyQueueRow {
  id: string;
  slug: string;
  title: string;
  type: string;
  blocks: string[];
  ownerId: string | null;
  /** Владелец-отдел (код) и его название. */
  ownerDeptCode?: string | null;
  ownerDeptLabel?: string | null;
  /** Человек или «Отдел: <название>» (для пустого/выключенного отдела — с пояснением). */
  ownerName: string | null;
  /** Есть кому подтверждать: человек либо включённый отдел с людьми. false = «без владельца». */
  hasOwner?: boolean;
  verifyEveryDays: number | null;
  verifiedAt: string | null;
  /** Срок следующей проверки (ISO), null — срок не задан. */
  dueAt: string | null;
  /** Дней до срока; отрицательное — просрочено. */
  daysLeft: number | null;
  viewCount: number;
  lastReadAt: string | null;
}
export interface VerifyQueueDto {
  overdue: VerifyQueueRow[];
  expiring: VerifyQueueRow[];
  noOwner: VerifyQueueRow[];
  counts: { overdue: number; expiring: number; noOwner: number };
}
export interface BulkResult {
  updated: number;
  failed: Array<{ id: string; error: string }>;
}

/** Эффективность обучения (фаза 3.7, L2–L4). */
export interface LearningEffectCourse {
  courseId: string;
  courseName: string;
  quizzes: number;
  learners: number;
  avgFirstScore: number;
  avgBestScore: number;
  firstTryPassPct: number;
  passPct: number;
  hardestQuestions: Array<{ questionId: string; text: string; wrong: number; total: number; wrongPct: number }>;
}
export interface PracticalEffectRow {
  skillId: string;
  skillName: string;
  courseName: string | null;
  trained: number;
  confirmed: number;
  confirmedPct: number;
  checks90d: number;
  checksPassed90d: number;
}
export type KpiCode = 'cogs_pct' | 'delivery_time_min' | 'late_arrivals';
export interface KpiCorrelationDto {
  kpi: KpiCode;
  kpiLabel: string;
  days: number;
  groups: { trained: { points: number; avgKpi: number | null }; others: { points: number; avgKpi: number | null } };
  rows: Array<{ tradingPointId: string; pointName: string; educationPct: number; kpi: number | null; group: 'trained' | 'others' }>;
}

/** Синонимы поиска (фаза 4.1). */
export interface SynonymDto {
  id: string;
  term: string;
  synonyms: string[];
  updatedAt: string;
}

export const wikiAdminApi = {
  categoryAudiences: () => api.get<Array<{ code: string; name: string; source: 'crm' | 'iiko' }>>('/wiki/admin/category-audiences'),
  listSynonyms: () => api.get<SynonymDto[]>('/wiki/admin/synonyms'),
  createSynonym: (dto: { term: string; synonyms: string[] }) => api.post<SynonymDto>('/wiki/admin/synonyms', dto),
  updateSynonym: (id: string, dto: { term?: string; synonyms?: string[] }) => api.patch<SynonymDto>(`/wiki/admin/synonyms/${id}`, dto),
  deleteSynonym: (id: string) => api.delete<{ ok: true }>(`/wiki/admin/synonyms/${id}`),
  listBlocks: () => api.get<AdminBlock[]>('/wiki/admin/blocks'),
  createBlock: (b: Partial<AdminBlock>) => api.post<AdminBlock>('/wiki/admin/blocks', b),
  updateBlock: (id: string, b: Partial<AdminBlock>) => api.patch<AdminBlock>(`/wiki/admin/blocks/${id}`, b),
  deleteBlock: (id: string) => api.delete<{ ok: true }>(`/wiki/admin/blocks/${id}`),
  listTags: () => api.get<AdminTag[]>('/wiki/admin/tags'),
  createTag: (t: Partial<AdminTag>) => api.post<AdminTag>('/wiki/admin/tags', t),
  updateTag: (id: string, t: Partial<AdminTag>) => api.patch<AdminTag>(`/wiki/admin/tags/${id}`, t),
  deleteTag: (id: string) => api.delete<{ ok: true }>(`/wiki/admin/tags/${id}`),

  // Подразделы (категории) — пустые, для structure-first наполнения
  createCategory: (blockCode: string, name: string) =>
    api.post<WikiCategoryDto>('/wiki/admin/categories', { blockCode, name }),
  renameCategory: (id: string, name: string) =>
    api.patch<WikiCategoryDto>(`/wiki/admin/categories/${id}`, { name }),
  /** Роли видимости подраздела (пусто = видно всем). */
  updateCategoryRoles: (id: string, roles: string[]) =>
    api.patch<WikiCategoryDto>(`/wiki/admin/categories/${id}/roles`, { roles }),
  deleteCategory: (id: string) => api.delete<{ ok: true }>(`/wiki/admin/categories/${id}`),
  /** Переставить порядок подразделов блока (sortOrder = index). */
  reorderCategories: (ids: string[]) =>
    api.post<{ ok: true }>('/wiki/admin/categories/reorder', { ids }),

  analytics: () =>
    api.get<{
      totalArticles: number;
      totalReads: number;
      topViewed: Array<{ slug: string; title: string; viewCount: number }>;
      topTime: Array<{ slug: string; title: string; totalMin: number }>;
      searchNotFound: Array<{ query: string; count: number }>;
      mostAcked: Array<{ slug: string; title: string; acked: number }>;
    }>('/wiki/admin/analytics'),
  whoRead: (slug: string) =>
    api.get<Array<{ name: string; firstReadAt: string; readCount: number; timeSpentSec: number; acked: boolean }>>(
      `/wiki/admin/analytics/who-read?slug=${encodeURIComponent(slug)}`,
    ),

  // ── Дашборд аналитики FISHKA HUB (сетевые KPI + графики) ──────────
  /** KPI-плитки: reach / readPct / eduPct / compliancePct. */
  analyticsOverview: () =>
    api.get<{ reach: number; readPct: number; eduPct: number; compliancePct: number; activeUsers: number }>(
      '/wiki/admin/analytics/overview',
    ),
  /** Прогресс обучения по ролям. */
  analyticsTrainingByRole: () =>
    api.get<Array<{ role: string; percent: number; users: number }>>('/wiki/admin/analytics/training-by-role'),
  /** Статусы стандартов по сети (пары юзер×материал). */
  analyticsStandardsStatus: () =>
    api.get<{ acked: number; pending: number; overdue: number }>('/wiki/admin/analytics/standards-status'),
  /**
   * Серия прочтений новостей. period — период дашборда:
   *   week → дневные столбцы (7 дн.), month → недельные (5 нед.),
   *   quarter → месячные (3 мес.). 'day' — легаси (14 дней).
   */
  analyticsNewsReads: (period: AnalyticsPeriod | 'day' = 'month') =>
    api.get<Array<{ label: string; value: number }>>(`/wiki/admin/analytics/news-reads?period=${period}`),
  /**
   * Помесячная динамика подтверждений стандартов. Глубина окна зависит от
   * периода: week→3 мес., month→6 мес., quarter→12 мес.
   */
  analyticsComplianceTrend: (period: AnalyticsPeriod = 'month') =>
    api.get<Array<{ label: string; acked: number }>>(`/wiki/admin/analytics/compliance-trend?period=${period}`),
  /**
   * Соответствие по точкам. Wiki-комплаенс атрибутируется через партнёра
   * точки (TradingPoint.partnerId → CRM-юзера партнёра). Точки одного
   * партнёра имеют одинаковые (партнёрские) показатели.
   */
  learningEffect: (courseId?: string | null) => api.get<LearningEffectCourse[]>(`/wiki/admin/analytics/learning-effect${courseId ? `?courseId=${encodeURIComponent(courseId)}` : ''}`),
  practicalEffect: () => api.get<PracticalEffectRow[]>('/wiki/admin/analytics/practical-effect'),
  kpiCorrelation: (kpi: KpiCode, days = 30) => api.get<KpiCorrelationDto>(`/wiki/admin/analytics/kpi-correlation?kpi=${kpi}&days=${days}`),
  analyticsPoints: () =>
    api.get<
      Array<{
        tradingPointId: string;
        pointName: string;
        partnerName: string;
        newsReadPct: number;
        educationPct: number;
        standardsPct: number;
        hasData: boolean;
        status: 'ok' | 'warning' | 'overdue' | 'no_data';
      }>
    >('/wiki/admin/analytics/points'),

  // Шаблоны документов (Q67-70)
  listDocTemplates: () => api.get<AdminDocTemplate[]>('/wiki/admin/document-templates'),
  getDocTemplate: (id: string) =>
    api.get<AdminDocTemplateDetail>(`/wiki/admin/document-templates/${encodeURIComponent(id)}`),
  createDocTemplate: (dto: {
    name: string;
    bodyMd: string;
    fields: DocTemplateField[];
    visibleToRoles?: string[];
    articleSlug?: string;
  }) => api.post<{ id: string }>('/wiki/admin/document-templates', dto),
  updateDocTemplate: (
    id: string,
    dto: {
      name?: string;
      bodyMd?: string;
      fields?: DocTemplateField[];
      visibleToRoles?: string[];
      articleSlug?: string | null;
    },
  ) => api.patch<{ ok: true }>(`/wiki/admin/document-templates/${encodeURIComponent(id)}`, dto),
  deleteDocTemplate: (id: string) =>
    api.delete<{ ok: true }>(`/wiki/admin/document-templates/${encodeURIComponent(id)}`),

  // Глобальный тоггл уведомлений (Q47/Q87)
  // Актуальность (фаза 3.4): очередь УК, кандидаты в архив, массовые действия.
  /** Завершения курсов по неделям — график в аналитике обучения. */
  completionsByWeek: () => api.get<Array<{ week: string; completed: number }>>('/wiki/admin/analytics/completions-by-week'),
  /** Вопросы, на которых чаще всего ошибаются. */
  hardQuestions: () =>
    api.get<Array<{ questionId: string; text: string; quizTitle: string | null; courseName: string | null; answered: number; wrong: number; wrongPct: number }>>(
      '/wiki/admin/analytics/hard-questions',
    ),
  verifyQueue: () => api.get<VerifyQueueDto>('/wiki/admin/verify-queue'),
  /** Только счётчики очереди актуальности — для бейджа в меню (раз в минуту). */
  verifyCounts: () => api.get<{ overdue: number; expiring: number; noOwner: number }>('/wiki/admin/verify-counts'),
  /** Сколько ответов ждёт ручной проверки — для бейджа в меню. */
  reviewsCount: () => api.get<number>('/wiki/admin/lms/reviews/count'),
  staleArticles: () => api.get<VerifyQueueRow[]>('/wiki/admin/stale'),
  /** Массово назначить владельца: сотрудника (`ownerId`) или отдел (`ownerDeptCode`). */
  assignOwner: (articleIds: string[], owner: { ownerId: string } | { ownerDeptCode: string }) =>
    api.post<BulkResult>('/wiki/admin/articles/owner', { articleIds, ...owner }),
  verifyBulk: (articleIds: string[]) => api.post<BulkResult>('/wiki/admin/articles/verify', { articleIds }),
  archiveBulk: (articleIds: string[], replacedBySlug?: string | null) =>
    api.post<{ archived: number; failed: Array<{ id: string; error: string }> }>('/wiki/admin/articles/archive', {
      articleIds,
      replacedBySlug: replacedBySlug ?? null,
    }),
  getGlobalNotify: () => api.get<{ enabled: boolean }>('/wiki/admin/notifications/global'),
  setGlobalNotify: (enabled: boolean) =>
    api.patch<{ enabled: boolean }>('/wiki/admin/notifications/global', { enabled }),
};

// ── Роли и пользователи (READ-ONLY проекция CRM-RBAC, kb:admin) ─────

/** Уровень доступа к разделу БЗ (0=Нет, 1=Просмотр, 2=Ред., 3=Публикация). */
export type KbAccessLevel = 0 | 1 | 2 | 3;

export interface RoleKbArea {
  key: string;
  label: string;
  level: KbAccessLevel;
}

/** Read-only DTO роли (экран «Роли и права»). Зеркало WikiRoleDto бэка. */
export interface RoleDto {
  code: string;
  name: string;
  isProtected: boolean;
  permissions: string[];
  userCount: number;
  kbAreas: RoleKbArea[];
  writeBlocks: Array<{ code: string; name: string; openToAll: boolean }>;
}

/** Read-only DTO пользователя wiki. Зеркало WikiUserDto бэка. */
export interface WikiUserDto {
  id: string;
  type: 'crm' | 'employee';
  fullName: string;
  email: string | null;
  phone: string | null;
  roleCode: string | null;
  roleName: string | null;
  scope: string | null;
  avatarUrl: string | null;
  status: 'active' | 'invited' | 'blocked';
  lastLoginAt: string | null;
}

/** Роли — read-only список с матрицей доступа к разделам БЗ (kb:admin). */
export const rolesAdminApi = {
  list: () => api.get<RoleDto[]>('/wiki/admin/roles'),
};

/** Пользователи wiki — read-only список (CRM + iiko-сотрудники), kb:admin. */
export const usersAdminApi = {
  list: (params: { search?: string; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.search) q.set('search', params.search);
    if (params.limit) q.set('limit', String(params.limit));
    const s = q.toString();
    return api.get<WikiUserDto[]>(`/wiki/admin/users${s ? `?${s}` : ''}`);
  },
};

// ── Decision trees (Q75-78) ─────────────────────────────────────────

export interface DecisionEdge {
  label: string;
  nextNodeId?: string;
  articleSlug?: string;
  url?: string;
}
export interface DecisionNode {
  id: string;
  kind: 'question' | 'action' | 'link';
  text: string;
  edges: DecisionEdge[];
}
export interface DecisionGraph {
  rootId: string;
  nodes: DecisionNode[];
}

export const decisionTreeApi = {
  /** Граф дерева по slug статьи (публичный read — для renderer'а). */
  get: (slug: string) =>
    api.get<DecisionGraph | null>(`/wiki/decision-tree/${encodeURIComponent(slug)}`),
  /** Залогировать путь пользователя по дереву (аналитика Q78). */
  logPath: (slug: string, path: string[], reachedLeaf: boolean) =>
    api.post<{ ok: true }>(`/wiki/decision-tree/${encodeURIComponent(slug)}/path`, { path, reachedLeaf }),
  /** Сохранить граф (admin builder, kb:admin). */
  save: (slug: string, graph: DecisionGraph) =>
    api.post<{ ok: true }>(`/wiki/admin/decision-tree/${encodeURIComponent(slug)}`, graph),
  /** Топ путей за период (admin аналитика). */
  topPaths: (slug: string) =>
    api.get<Array<{ path: string[]; count: number }>>(
      `/wiki/admin/decision-tree/${encodeURIComponent(slug)}/top-paths`,
    ),
};

// ── Документы (Q67-70) ──────────────────────────────────────────────

export interface DocTemplateField {
  key: string;
  label: string;
  type?: 'text' | 'textarea' | 'date' | 'number';
  required?: boolean;
  defaultFromContext?: string;
  placeholder?: string;
}
export interface DocTemplateListItem {
  id: string;
  name: string;
  fieldsCount: number;
  articleSlug: string | null;
}
export interface DocTemplateForFill {
  id: string;
  name: string;
  fields: DocTemplateField[];
  defaults: Record<string, string>;
}
export interface MyDocumentItem {
  id: string;
  templateName: string;
  authorName: string;
  createdAt: string;
  isMine: boolean;
}
export interface AdminDocTemplate {
  id: string;
  name: string;
  fields: DocTemplateField[];
  visibleToRoles: string[];
  articleSlug: string | null;
}
export interface AdminDocTemplateDetail {
  id: string;
  name: string;
  bodyMd: string;
  fields: DocTemplateField[];
  visibleToRoles: string[];
}

export const documentsApi = {
  listTemplates: () => api.get<DocTemplateListItem[]>('/wiki/documents/templates'),
  getTemplate: (id: string) =>
    api.get<DocTemplateForFill>(`/wiki/documents/templates/${encodeURIComponent(id)}`),
  fill: (id: string, values: Record<string, string>) =>
    api.post<{ id: string }>(`/wiki/documents/templates/${encodeURIComponent(id)}/fill`, { values }),
  listMine: () => api.get<MyDocumentItem[]>('/wiki/documents/mine'),
  /** URL для скачивания/просмотра PDF (window.open, cookie same-origin). */
  pdfUrl: (id: string) => `/api/wiki/documents/${encodeURIComponent(id)}/pdf`,
};

// ── Уведомления + Web Push (Q47/Q58/Q87-90) ─────────────────────────

export interface WikiNotifPrefs {
  globalEnabled: boolean;
  blocks: Array<{ blockId: string; code: string; name: string; enabled: boolean }>;
  quietHoursStart: string | null;
  quietHoursEnd: string | null;
  digestMode: string;
}

/** In-app уведомление (колокол в шапке). */
export interface WikiInboxNotification {
  id: string;
  type: string;
  title: string;
  body: string | null;
  link: string | null;
  isRead: boolean;
  createdAt: string;
}

export const notificationsApi = {
  vapidKey: () => api.get<{ key: string | null }>('/wiki/notifications/vapid-key'),
  subscribe: (sub: { endpoint: string; keys: { p256dh: string; auth: string } }) =>
    api.post<{ ok: true }>('/wiki/notifications/push/subscribe', sub),
  unsubscribe: (endpoint: string) =>
    api.post<{ ok: true }>('/wiki/notifications/push/unsubscribe', { endpoint }),
  getPrefs: () => api.get<WikiNotifPrefs>('/wiki/notifications/prefs'),
  setPrefs: (patch: { quietHoursStart?: string | null; quietHoursEnd?: string | null; digestMode?: string }) =>
    api.patch<{ ok: true }>('/wiki/notifications/prefs', patch),
  setBlock: (blockId: string, enabled: boolean) =>
    api.patch<{ ok: true }>(`/wiki/notifications/blocks/${encodeURIComponent(blockId)}`, { enabled }),

  // ── In-app инбокс (колокол) ──────────────────────────────────────
  /** Список уведомлений (unread=true — только непрочитанные). */
  inbox: (unreadOnly = false) =>
    api.get<WikiInboxNotification[]>(`/wiki/notifications/inbox${unreadOnly ? '?unread=1' : ''}`),
  /** Счётчик непрочитанных (для бейджа). */
  inboxCount: () => api.get<{ count: number }>('/wiki/notifications/inbox/count'),
  /** Отметить одно прочитанным. */
  inboxRead: (id: string) =>
    api.post<{ ok: true }>(`/wiki/notifications/inbox/${encodeURIComponent(id)}/read`),
  /** Отметить все прочитанными. */
  inboxReadAll: () => api.post<{ ok: true }>('/wiki/notifications/inbox/read-all'),
};

// ── Параллельная загрузка больших файлов ────────────────────────────
//
// Один TCP-поток на канале с задержкой ~100 мс не утилизирует ширину канала
// (упирается в TCP-окно) — реальная отдача ~50% от линка. Бьём файл на части
// и грузим их в несколько потоков параллельно (как менеджеры загрузок), бэкенд
// склеивает. Возвращаем {uploadId,totalParts} для finalize-эндпоинта.

/** Размер части и число параллельных потоков (подобрано под средний канал). */
const UPLOAD_PART_SIZE = 8 * 1024 * 1024; // 8 МБ
const UPLOAD_CONCURRENCY = 4;

export type UploadProgress = (percent: number) => void;

async function uploadFileInParts(
  file: File,
  onProgress?: UploadProgress,
): Promise<{ uploadId: string; totalParts: number }> {
  const uploadId = crypto.randomUUID();
  const totalParts = Math.max(1, Math.ceil(file.size / UPLOAD_PART_SIZE));
  let uploadedBytes = 0;
  let nextIndex = 0;

  const worker = async (): Promise<void> => {
    for (;;) {
      const i = nextIndex;
      nextIndex += 1;
      if (i >= totalParts) return;
      const start = i * UPLOAD_PART_SIZE;
      const blob = file.slice(start, Math.min(start + UPLOAD_PART_SIZE, file.size));
      const fd = new FormData();
      fd.append('file', blob, `${i}.part`);
      const r = await fetch(
        `/api/wiki/uploads/${encodeURIComponent(uploadId)}/parts/${i}`,
        { method: 'POST', credentials: 'include', body: fd },
      );
      if (!r.ok) {
        const b = (await r.json().catch(() => null)) as { message?: string } | null;
        throw new Error(b?.message || `HTTP ${r.status}`);
      }
      uploadedBytes += blob.size;
      onProgress?.(Math.min(99, Math.round((uploadedBytes / file.size) * 100)));
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(UPLOAD_CONCURRENCY, totalParts) }, () => worker()),
  );
  return { uploadId, totalParts };
}

// ── SCORM-курсы в уроках (владелец 04.10.2026, DocsInBox) ──────────

export type ScormPackageStatus = 'uploaded' | 'processing' | 'ready' | 'error';

export interface ScormPackageMeta {
  id: string;
  status: ScormPackageStatus;
  error: string | null;
  title: string | null;
  version: '1.2' | '2004' | null;
  launchPath: string | null;
  masteryScore: number | null;
  fileName: string;
  fileCount: number | null;
  totalBytes: number | null;
  lessonId: string | null;
  createdAt: string;
}

export interface ScormRuntimeState {
  progress: ScormSavedProgress | null;
  learner: { id: string; name: string };
  version: '1.2' | '2004' | null;
  masteryScore: number | null;
}

export interface ScormSaveResult {
  ok: true;
  outcome: 'passed' | 'failed' | 'completed' | null;
  lessonCompleted: boolean;
  courseCompleted: boolean;
}

export interface ScormResultRow {
  packageId: string;
  packageLabel: string;
  isCurrent: boolean;
  ownerKey: string;
  name: string;
  kind: 'crm' | 'employee';
  completionStatus: string;
  successStatus: string;
  outcome: 'passed' | 'failed' | 'completed' | null;
  scorePercent: number | null;
  scoreRaw: number | null;
  scoreMax: number | null;
  totalTimeSec: number;
  attempts: number;
  completedAt: string | null;
  updatedAt: string;
}

export const scormApi = {
  /** Загрузить zip курса в урок (части в несколько потоков) → пакет в очереди распаковки. */
  uploadToLesson: async (
    lessonId: string,
    file: File,
    onProgress?: UploadProgress,
  ): Promise<{ id: string; status: ScormPackageStatus; fileName: string }> => {
    const { uploadId, totalParts } = await uploadFileInParts(file, onProgress);
    const r = await fetch(`/api/wiki/lessons/${encodeURIComponent(lessonId)}/scorm/parallel-finalize`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ uploadId, totalParts, fileName: file.name }),
    });
    if (!r.ok) {
      const b = (await r.json().catch(() => null)) as { message?: string } | null;
      throw new Error(b?.message || `HTTP ${r.status}`);
    }
    onProgress?.(100);
    return r.json() as Promise<{ id: string; status: ScormPackageStatus; fileName: string }>;
  },
  meta: (pkgId: string) => api.get<ScormPackageMeta>(`/wiki/scorm/${encodeURIComponent(pkgId)}/meta`),
  runtime: (pkgId: string) => api.get<ScormRuntimeState>(`/wiki/scorm/${encodeURIComponent(pkgId)}/runtime`),
  save: (pkgId: string, body: Record<string, unknown>) =>
    api.post<ScormSaveResult>(`/wiki/scorm/${encodeURIComponent(pkgId)}/runtime`, body),
  /** При закрытии страницы: sendBeacon (text/plain — бэк разбирает сам). */
  beacon: (pkgId: string, body: Record<string, unknown>): boolean => {
    const url = `/api/wiki/scorm/${encodeURIComponent(pkgId)}/runtime`;
    const data = JSON.stringify(body);
    try {
      if (navigator.sendBeacon && navigator.sendBeacon(url, new Blob([data], { type: 'text/plain;charset=UTF-8' }))) return true;
    } catch {
      /* ниже — keepalive-fetch */
    }
    void fetch(url, { method: 'POST', credentials: 'include', keepalive: true, headers: { 'Content-Type': 'application/json' }, body: data }).catch(() => undefined);
    return false;
  },
  /** Конструктор курса: текущая версия + последняя загрузка + число результатов. */
  lessonInfo: (lessonId: string) =>
    api.get<{ current: ScormPackageMeta | null; latest: ScormPackageMeta | null; resultsCount: number }>(
      `/wiki/lessons/${encodeURIComponent(lessonId)}/scorm`,
    ),
  lessonResults: (lessonId: string) =>
    api.get<{ masteryScore: number | null; items: ScormResultRow[] }>(`/wiki/lessons/${encodeURIComponent(lessonId)}/scorm/results`),
};

// ── Вложения (Q29-32) ───────────────────────────────────────────────

export const attachmentsApi = {
  /**
   * Загрузка файла (multipart). file — нативный File из <input>.
   * diskFolderId — если PDF: бэк дублирует копию в Диск в эту папку.
   */
  upload: async (
    articleId: string,
    file: File,
    diskFolderId?: string,
  ): Promise<{ id: string; fileName: string; kind: string; diskFileId?: string }> => {
    const fd = new FormData();
    fd.append('file', file);
    if (diskFolderId) fd.append('diskFolderId', diskFolderId);
    const r = await fetch(`/api/wiki/articles/${encodeURIComponent(articleId)}/attachments`, {
      method: 'POST',
      credentials: 'include',
      body: fd,
    });
    if (!r.ok) {
      const b = (await r.json().catch(() => null)) as { message?: string } | null;
      throw new Error(b?.message || `HTTP ${r.status}`);
    }
    return r.json() as Promise<{ id: string; fileName: string; kind: string; diskFileId?: string }>;
  },
  /**
   * Параллельная загрузка вложения (для больших файлов): бьём на части, грузим
   * в несколько потоков, бэкенд склеивает. onProgress(percent) — для прогресс-бара.
   */
  uploadParallel: async (
    articleId: string,
    file: File,
    onProgress?: UploadProgress,
    // inline=true для картинок, вставляемых прямо в текст из редактора — такие
    // не показываются в панели «Вложения». Кнопка «Загрузить» во «Вложениях»
    // шлёт false (отдельный прикреплённый файл).
    inline = false,
  ): Promise<{ id: string; fileName: string; kind: string; diskFileId?: string }> => {
    const { uploadId, totalParts } = await uploadFileInParts(file, onProgress);
    const r = await fetch(
      `/api/wiki/articles/${encodeURIComponent(articleId)}/attachments/parallel-finalize`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uploadId, totalParts, fileName: file.name, mimeType: file.type, inline }),
      },
    );
    if (!r.ok) {
      const b = (await r.json().catch(() => null)) as { message?: string } | null;
      throw new Error(b?.message || `HTTP ${r.status}`);
    }
    onProgress?.(100);
    return r.json() as Promise<{ id: string; fileName: string; kind: string; diskFileId?: string }>;
  },
  /** diskTrashed — файлы, ушедшие вслед за вложением в Корзину Диска (30 дней). */
  delete: (id: string) =>
    api.delete<{ ok: true; diskTrashed?: TrashedDiskFile[] }>(`/wiki/attachments/${encodeURIComponent(id)}`),
  /** Переименовать вложение: то же имя получает его копия на Диске. */
  rename: (id: string, fileName: string) =>
    api.patch<{ ok: true }>(`/wiki/attachments/${encodeURIComponent(id)}`, { fileName }),
  /** Пометить вложение inline (скрыть из «Вложений», оставив в тексте статьи). */
  setInline: (id: string, inline = true) =>
    api.patch<{ ok: true }>(`/wiki/attachments/${encodeURIComponent(id)}/inline`, { inline }),
  /** URL для скачивания/просмотра вложения. */
  downloadUrl: (id: string) => `/api/wiki/attachments/${encodeURIComponent(id)}/download`,
};

// ── Вопросы партнёров (FISHKA HUB) ──────────────────────────────────

export interface WikiQuestionDto {
  id: string;
  question: string;
  askedByName: string | null;
  blockId: string | null;
  status: string;
  answer: string | null;
  answeredByName: string | null;
  answeredAt: string | null;
  resultArticleId: string | null;
  createdAt: string;
}

export const questionsApi = {
  ask: (question: string, blockId?: string | null) =>
    api.post<{ id: string }>('/wiki/questions', { question, blockId: blockId ?? null }),
  mine: () => api.get<WikiQuestionDto[]>('/wiki/questions/mine'),
  // Инбокс УК (kb:admin)
  inbox: (status?: string) =>
    api.get<WikiQuestionDto[]>(`/wiki/admin/questions${status ? `?status=${encodeURIComponent(status)}` : ''}`),
  stats: () => api.get<{ open: number }>('/wiki/admin/questions/stats'),
  answer: (id: string, answer: string) =>
    api.post<{ ok: true }>(`/wiki/admin/questions/${encodeURIComponent(id)}/answer`, { answer }),
  archive: (id: string) =>
    api.post<{ ok: true }>(`/wiki/admin/questions/${encodeURIComponent(id)}/archive`),
};

// ── Стандарты (FISHKA HUB) ──────────────────────────────────────────

export interface StandardItemDto {
  id: string;
  slug: string;
  title: string;
  category: string;
  blockCode: string | null;
  version: number;
  versionLabel: string;
  isAcked: boolean;
  ackedVersion: number | null;
  hasQuiz: boolean;
  downloadable: boolean;
  effectiveFrom: string | null;
  currentEffectiveVersion: number;
  currentEffectiveLabel: string;
  hasUpcoming: boolean;
  upcomingEffectiveFrom: string | null;
  updatedAt: string;
  /** Группа-блок (FISHKA HUB). NULL → «Без блока». */
  standardGroupId: string | null;
  /** Порядок внутри группы-блока (drag-реордер). */
  sortOrder: number;
}
/** Группа-блок дерева «Стандартов сети» (плоско, с parentId). */
export interface StandardGroupDto {
  id: string;
  name: string;
  parentId: string | null;
  sortOrder: number;
  count: number;
}
export interface StandardsResponseDto {
  stats: { total: number; acknowledged: number; pending: number; overdue: number; percent: number };
  categories: string[];
  groups: StandardGroupDto[];
  items: StandardItemDto[];
}

export const standardsApi = {
  get: () => api.get<StandardsResponseDto>('/wiki/standards'),
  // CRUD групп-блоков + назначение стандарта в группу (kb:admin).
  createGroup: (body: { name: string; parentId?: string | null }) =>
    api.post<StandardGroupDto>('/wiki/standards/groups', body),
  renameGroup: (id: string, name: string) =>
    api.patch<{ ok: true }>(`/wiki/standards/groups/${encodeURIComponent(id)}`, { name }),
  deleteGroup: (id: string) =>
    api.delete<{ ok: true }>(`/wiki/standards/groups/${encodeURIComponent(id)}`),
  /** Перенести стандарт в блок/папку (groupId=null → «Без блока») + порядок. */
  assignGroup: (id: string, body: { groupId?: string | null; sortOrder?: number }) =>
    api.patch<{ ok: true }>(`/wiki/standards/items/${encodeURIComponent(id)}/group`, body),
};

// ── Диск (FISHKA HUB) — НАСТОЯЩИЕ папки + файлы (отдельная модель) ────

/** Папка диска (вложенная — дерево через parentId). */
export interface DiskFolderDto {
  id: string;
  name: string;
  icon: string | null;
  color: string;
  sortOrder: number;
  /** Родительская папка (дерево). NULL → раздел верхнего уровня. */
  parentId: string | null;
  fileCount: number;
  totalSize: number;
  createdAt: string;
}

/** Файл диска — не привязан к статье (в отличие от вложений). */
export interface DiskFileDto {
  id: string;
  folderId: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  uploadedById: string | null;
  uploadedByName: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Файл Диска, ушедший в Корзину вслед за правкой статьи / удалением вложения. */
export interface TrashedDiskFile {
  id: string;
  fileName: string;
  folderId: string;
  folderName: string | null;
}

/** Строка Корзины Диска: удалённое за последние 30 дней. */
export interface DiskTrashItemDto {
  id: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  folderId: string;
  /** «Кухня › Стандарты для печати» — где файл лежал. */
  folderPath: string;
  deletedAt: string;
  deletedById: string | null;
  deletedByName: string | null;
  /** manual | article_removed | attachment_deleted */
  reason: string;
  reasonLabel: string;
  /** Когда файл исчезнет из Корзины окончательно. */
  expiresAt: string;
  /** Файл появился из вложения статьи (а не загружен на Диск напрямую). */
  fromArticle: boolean;
}

export const diskApi = {
  // Папки
  folders: () => api.get<DiskFolderDto[]>('/wiki/disk/folders'),
  createFolder: (body: { name: string; icon?: string | null; color?: string | null; parentId?: string | null }) =>
    api.post<DiskFolderDto>('/wiki/disk/folders', body),
  updateFolder: (id: string, body: { name?: string; icon?: string | null; color?: string | null }) =>
    api.patch<{ ok: true }>(`/wiki/disk/folders/${encodeURIComponent(id)}`, body),
  deleteFolder: (id: string) =>
    api.delete<{ ok: true }>(`/wiki/disk/folders/${encodeURIComponent(id)}`),
  // Файлы
  files: (folderId: string) =>
    api.get<DiskFileDto[]>(`/wiki/disk/folders/${encodeURIComponent(folderId)}/files`),
  /** Загрузка файла в папку (multipart). file — нативный File из <input>. */
  upload: async (folderId: string, file: File): Promise<DiskFileDto> => {
    const fd = new FormData();
    fd.append('file', file);
    const r = await fetch(`/api/wiki/disk/folders/${encodeURIComponent(folderId)}/files`, {
      method: 'POST',
      credentials: 'include',
      body: fd,
    });
    if (!r.ok) {
      const b = (await r.json().catch(() => null)) as { message?: string } | null;
      throw new Error(b?.message || `HTTP ${r.status}`);
    }
    return r.json() as Promise<DiskFileDto>;
  },
  /**
   * Параллельная загрузка файла в папку (для больших файлов): бьём на части,
   * грузим в несколько потоков, бэкенд склеивает. onProgress(percent) — прогресс.
   */
  uploadParallel: async (
    folderId: string,
    file: File,
    onProgress?: UploadProgress,
  ): Promise<DiskFileDto> => {
    const { uploadId, totalParts } = await uploadFileInParts(file, onProgress);
    const r = await fetch(
      `/api/wiki/disk/folders/${encodeURIComponent(folderId)}/parallel-finalize`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uploadId, totalParts, fileName: file.name, mimeType: file.type }),
      },
    );
    if (!r.ok) {
      const b = (await r.json().catch(() => null)) as { message?: string } | null;
      throw new Error(b?.message || `HTTP ${r.status}`);
    }
    onProgress?.(100);
    return r.json() as Promise<DiskFileDto>;
  },
  updateFile: (id: string, body: { fileName?: string; folderId?: string }) =>
    api.patch<{ ok: true }>(`/wiki/disk/files/${encodeURIComponent(id)}`, body),
  /** «Удалить» = в Корзину на 30 дней; вложение-источник убирается из «Вложений» статьи. */
  deleteFile: (id: string) =>
    api.delete<{ ok: true; attachmentRemoved: boolean; articleTitle: string | null }>(`/wiki/disk/files/${encodeURIComponent(id)}`),
  // Корзина (30 дней)
  trash: () => api.get<DiskTrashItemDto[]>('/wiki/disk/trash'),
  restoreFile: (id: string) => api.post<DiskFileDto>(`/wiki/disk/trash/${encodeURIComponent(id)}/restore`),
  /** «Удалить навсегда» — только из Корзины. */
  purgeFile: (id: string) => api.delete<{ ok: true }>(`/wiki/disk/trash/${encodeURIComponent(id)}`),
  /** URL для скачивания/просмотра файла диска. */
  downloadUrl: (id: string) => `/api/wiki/disk/files/${encodeURIComponent(id)}/download`,
};

// ── LMS-конструктор (kb:admin) ──────────────────────────────────────

/** multipart POST через XHR — ради прогресса загрузки (видео до 1 ГБ). */
function xhrUpload<T>(url: string, body: FormData, onProgress: (pct: number) => void): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.withCredentials = true;
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.min(99, Math.round((e.loaded / e.total) * 100)));
    };
    xhr.onload = () => {
      let parsed: unknown = null;
      try {
        parsed = JSON.parse(xhr.responseText);
      } catch { /* не JSON */ }
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress(100);
        resolve(parsed as T);
        return;
      }
      const m = (parsed as { message?: string | string[] } | null)?.message;
      reject(new Error(m ? (Array.isArray(m) ? m.join(', ') : m) : xhr.status === 413 ? 'Файл слишком большой' : `Ошибка загрузки (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error('Сеть оборвалась во время загрузки'));
    xhr.send(body);
  });
}

/** Зона нажатия в % от картинки/кадра (на телефоне и большом экране — на том же месте). */
export interface TrainerZone { xPct: number; yPct: number; wPct: number; hPct: number }
/** Куда встаёт подсказка; auto — сама (по положению зоны). */
export type TrainerPlacement = 'auto' | 'top' | 'bottom' | 'left' | 'right';
/**
 * Что ждём от ученика (шаг со скриншотом и остановка видео — один набор):
 * левый/правый/двойной клик, навести, перетащить (зона → цель), ввести
 * текст, выбрать из списка, клавиша/сочетание, найти все места, вопрос,
 * просто прочитать.
 */
export type TrainerAction = 'click' | 'right_click' | 'double_click' | 'hover' | 'drag' | 'type' | 'select' | 'key' | 'multi' | 'quiz' | 'info';
export interface TrainerQuiz { question: string; options: string[]; correctIndex: number }
/** Выпадающий список на месте зоны: варианты и верный. */
export interface TrainerChoice { options: string[]; correctIndex: number }
/** Общие поля задания шага и остановки видео. */
export interface TrainerTask {
  hint?: string;
  tooltipPos?: TrainerPlacement;
  /** Зона действия (у «перетащить» — откуда). */
  hotspot?: TrainerZone;
  /** Нет поля (старые) — у шага: click, если есть скриншот и зона, иначе info; у остановки — по kind. */
  action?: TrainerAction;
  /** «Перетащить»: куда отпустить. */
  target?: TrainerZone;
  /** «Найти все места»: все зоны. */
  zones?: TrainerZone[];
  /** «Ввести текст»: правильный ввод, варианты через «|». */
  expected?: string;
  /** «Выбрать из списка». */
  choice?: TrainerChoice;
  /** «Клавиша/сочетание»: Enter, Esc, Tab, Ctrl+S… */
  keys?: string;
  /** «Вопрос с вариантами». */
  quiz?: TrainerQuiz;
}
export interface TrainerStep extends TrainerTask {
  hint: string;
  /** Скриншот: вложение урока (/api/wiki/attachments/<id>/download) или внешняя ссылка (старые тренажёры). */
  imageUrl?: string;
  imageAttachmentId?: string;
  /** Ширина/высота скриншота (пишется при загрузке) — плеер ставит рамку до загрузки картинки. */
  imageAspect?: number;
}
/** Остановка видео-урока: видео замирает, дальше — только после верного действия/ответа. */
export interface TrainerVideoStop extends TrainerTask {
  id: string;
  atSec: number;
  /** Старое поле: quiz — вопрос, иначе click. Новые остановки пишут и action. */
  kind: 'click' | 'quiz';
}
/** Подсказка на видео: в промежутке с–по светится рамка и висит пояснение, видео не стоит. */
export interface TrainerCallout {
  id: string;
  fromSec: number;
  toSec: number;
  text: string;
  hotspot?: TrainerZone;
  tooltipPos?: TrainerPlacement;
}
/** Глава видео: метка с названием, у ученика — оглавление пройденных частей. */
export interface TrainerChapter { id: string; atSec: number; title: string }
export interface TrainerVideo {
  attachmentId?: string;
  url?: string;
  fileName?: string;
  durationSec?: number;
  /** Размер кадра (снимается в конструкторе) — плеер держит пропорции до загрузки видео. */
  width?: number;
  height?: number;
  /** Обложка: кадр видео, снятый в конструкторе (вложение урока). */
  posterUrl?: string;
  posterAttachmentId?: string;
  stops: TrainerVideoStop[];
  callouts?: TrainerCallout[];
  chapters?: TrainerChapter[];
}
export interface TrainerConfig {
  intro?: string;
  /** Нет поля — шаги со скриншотами (как раньше). */
  mode?: 'steps' | 'video';
  steps: TrainerStep[];
  video?: TrainerVideo;
  checkpoint?: TrainerQuiz;
  materials?: Array<{ name: string; type: string; url?: string }>;
}
/** Урок в разделе курса (фаза 2.1) — краткая карточка для конструктора. */
export type LessonType = 'article' | 'video' | 'quiz' | 'trainer' | 'practical' | 'survey' | 'external' | 'scorm';
export interface AdminLesson {
  id: string; type: LessonType; title: string; sortOrder: number;
  estimatedMinutes: number | null; hasBody: boolean;
  articleId: string | null; articleTitle: string | null; articleSlug: string | null;
  quizId: string | null; quizTitle: string | null;
  videoAttachmentId: string | null; videoUrl: string | null; externalUrl: string | null;
  hasTrainer: boolean;
}
/** Урок целиком (редактор). */
export interface AdminLessonFull {
  id: string; moduleId: string; moduleTitle: string; courseId: string;
  type: LessonType; title: string; bodyMd: string;
  articleId: string | null; articleTitle: string | null; articleSlug: string | null;
  quizId: string | null; quizTitle: string | null;
  videoAttachmentId: string | null;
  video: { id: string; fileName: string; status: string | null; durationSec: number | null; posterUrl: string | null } | null;
  videoUrl: string | null; externalUrl: string | null;
  /** Урок type=scorm: текущая версия курса. */
  scormPackageId: string | null;
  estimatedMinutes: number | null; objectives: string[];
  trainer: TrainerConfig | null; sortOrder: number;
  attachments: Array<{ id: string; fileName: string; kind: string; url: string }>;
}
export interface SurveyQuestionInput { text: string; kind: 'scale' | 'text'; scale?: number }
export interface LessonInput {
  title?: string; type?: LessonType; bodyMd?: string;
  articleId?: string | null; quizId?: string | null; videoAttachmentId?: string | null;
  videoUrl?: string | null; externalUrl?: string | null; estimatedMinutes?: number | null;
  objectives?: string[]; trainer?: TrainerConfig | { questions: SurveyQuestionInput[] } | null;
}
export interface AdminCourseModule {
  id: string; title: string; sortOrder: number;
  articleId: string | null; articleTitle: string | null; articleSlug: string | null;
  quizId: string | null; quizTitle: string | null;
  videoUrl?: string | null;
  hasTrainer?: boolean; trainer?: TrainerConfig | null;
  /** Уроки раздела (фаза 2.1). */
  lessons: AdminLesson[];
}
/** Статус-воркфлоу курса (кит «LMS»). */
export type CourseStatus = 'DRAFT' | 'REVIEW' | 'PUBLISHED' | 'ARCHIVED';
export interface AdminCourse {
  id: string; name: string; description: string | null; forRoles: string[]; isRequired: boolean; sortOrder: number;
  status?: CourseStatus;
  level?: string | null; durationLabel?: string | null; coverKey?: string | null; hasCertificate?: boolean;
  /** Обложка (16.10.2026): фон плитки #RRGGBB и подпись на ней; coverKey — brand | emoji:… | icon:<uuid>. */
  coverColor?: string | null; coverText?: string | null;
  objectives?: string[]; estimatedMinutes?: number | null; recertifyEveryDays?: number | null;
  modules: AdminCourseModule[];
}
/** Типы вопросов (фаза 2.2). */
export type QuestionKind = 'single' | 'multi' | 'true_false' | 'matching' | 'ordering' | 'open' | 'hotspot' | 'photo_upload';
export interface AdminQuizQuestion {
  id: string; kind: QuestionKind; text: string;
  options: Array<{ id: string; text: string; isCorrect: boolean }>;
  payload: Record<string, unknown> | null; explanation: string | null; points: number; sortOrder: number;
}
export interface QuestionInput {
  text?: string; kind?: QuestionKind;
  options?: Array<{ id?: string; text: string; isCorrect: boolean }>;
  payload?: Record<string, unknown> | null; explanation?: string | null; points?: number;
}
export interface AdminQuiz {
  id: string; title: string; passingScore: number;
  maxAttempts: number | null; cooldownMinutes: number; timeLimitMinutes: number | null;
  showCorrectAnswers: boolean; shuffleQuestions: boolean; shuffleOptions: boolean;
  pickFromBank: number | null; bankId: string | null; bankName: string | null; bankQuestions: number;
  questions: AdminQuizQuestion[];
}
export interface QuizPolicyInput {
  title?: string; passingScore?: number; maxAttempts?: number | null; cooldownMinutes?: number;
  timeLimitMinutes?: number | null; showCorrectAnswers?: boolean; shuffleQuestions?: boolean; shuffleOptions?: boolean;
  pickFromBank?: number | null; bankId?: string | null;
}
export interface AdminBank { id: string; name: string; description: string | null; tags: string[]; questions: number; quizzes: number }
/** Траектории обучения (фаза 2.4). */
export type PathStepKind = 'course' | 'standard' | 'checklist' | 'assessment';
export interface AdminPathStep {
  id: string; kind: PathStepKind; title: string | null;
  courseId: string | null; courseName: string | null;
  articleId: string | null; articleTitle: string | null; articleSlug: string | null;
  dueOffsetDays: number | null; prerequisiteIndex: number | null; sortOrder: number;
}
export interface AdminPath {
  id: string; name: string; description: string | null; forRoles: string[]; autoAssign: boolean; isActive: boolean; sortOrder: number;
  assignments: number; completed: number; steps: AdminPathStep[];
}
export interface PathInput { name?: string; description?: string | null; forRoles?: string[]; autoAssign?: boolean; isActive?: boolean }
export interface PathStepInput { kind: PathStepKind; title?: string | null; courseId?: string | null; articleId?: string | null; dueOffsetDays?: number | null; prerequisiteIndex?: number | null }
export interface PathAssignmentRow { id: string; ownerKey: string; name: string; kind: 'crm' | 'emp'; assignedAt: string; dueAt: string | null; completedAt: string | null; source: string }
export interface MyPathStep { id: string; kind: PathStepKind; title: string; courseId: string | null; articleSlug: string | null; dueAt: string | null; done: boolean; locked: boolean; progressPercent: number | null }
export interface MyPathDto {
  assignmentId: string; pathId: string; name: string; description: string | null;
  assignedAt: string; dueAt: string | null; completedAt: string | null;
  totalSteps: number; doneSteps: number; nextStepId: string | null; steps: MyPathStep[];
  forRoles: string[]; autoAssign: boolean;
}
/** Навыки (фаза 3.1). */
export interface AdminSkill {
  id: string; code: string; name: string; description: string | null; roleCodes: string[];
  courseId: string | null; courseName: string | null; quizId: string | null; quizTitle: string | null;
  checklistArticleId: string | null; checklistArticleTitle: string | null; checklistArticleSlug: string | null;
  expiresDays: number | null; isActive: boolean; sortOrder: number; people: number;
}
export interface SkillInput { code?: string; name?: string; description?: string | null; roleCodes?: string[]; courseId?: string | null; quizId?: string | null; checklistArticleId?: string | null; expiresDays?: number | null; isActive?: boolean }
export interface MySkill { id: string; code: string; name: string; level: number; levelName: string; expiresAt: string | null; expired: boolean; courseId: string | null; checklistSlug: string | null }
export interface MentorTeam {
  point: string | null;
  skills: Array<{ id: string; code: string; name: string; roleCodes: string[]; hasChecklist: boolean }>;
  members: Array<{ ownerKey: string; name: string; roleCode: string; roleName: string; tradingPointId: string | null; hasAccount: boolean; skills: Array<{ skillId: string; level: number; expiresAt: string | null; expired: boolean }> }>;
}
export interface SkillsMatrix { total: number; skills: Array<{ id: string; name: string; levels: number[]; people: number }> }
/** Очередь ручной проверки (фаза 2.3). */
export interface ReviewQueueItem {
  attemptId: string; status: string; attemptedAt: string; score: number; passed: boolean;
  /** CRM-юзер (id = users.id) или сотрудник iiko (id = employee_accounts.id). */
  user: { id: string; ownerKey?: string; kind?: 'crm' | 'employee'; fullName: string; roleName: string | null };
  quiz: { id: string; title: string; passingScore: number };
  course: { id: string; name: string } | null;
  lesson: { id: string; title: string } | null;
  questions: Array<{
    questionId: string; kind: QuestionKind; text: string; points: number; hint: string; sample: string;
    answer: { text: string | null; attachmentId: string | null; url: string | null };
    review: { score: number; comment: string | null; reviewedAt: string } | null;
  }>;
}
export interface AdminBankFull { id: string; name: string; description: string | null; tags: string[]; questions: AdminQuizQuestion[] }

/** Назначение курса: цели (роли/юзеры/точки) + единый дедлайн. */
export interface CourseAssignmentDto {
  roleCodes: string[];
  userIds: string[];
  pointIds: string[];
  dueAt: string | null;
}

export const lmsAdminApi = {
  pointsRating: () => api.get<PointsRatingRow[]>('/wiki/admin/lms/gamification/points-rating'),
  listCourses: () => api.get<AdminCourse[]>('/wiki/admin/lms/courses'),
  createCourse: (dto: { name: string; description?: string; forRoles?: string[]; isRequired?: boolean; level?: string | null; durationLabel?: string | null; coverKey?: string | null; coverColor?: string | null; coverText?: string | null; hasCertificate?: boolean }) =>
    api.post<{ id: string }>('/wiki/admin/lms/courses', dto),
  updateCourse: (id: string, dto: { name?: string; description?: string | null; forRoles?: string[]; isRequired?: boolean; level?: string | null; durationLabel?: string | null; coverKey?: string | null; coverColor?: string | null; coverText?: string | null; hasCertificate?: boolean; objectives?: string[]; estimatedMinutes?: number | null; recertifyEveryDays?: number | null }) =>
    api.patch<{ ok: true }>(`/wiki/admin/lms/courses/${encodeURIComponent(id)}`, dto),
  deleteCourse: (id: string) => api.delete<{ ok: true }>(`/wiki/admin/lms/courses/${encodeURIComponent(id)}`),
  /** Сменить статус курса (воркфлоу DRAFT/REVIEW/PUBLISHED/ARCHIVED). */
  setCourseStatus: (id: string, status: CourseStatus) =>
    api.patch<{ ok: true }>(`/wiki/admin/lms/courses/${encodeURIComponent(id)}/status`, { status }),
  /** Назначить курс (роли/юзеры/точки + дедлайн). Идемпотентно (delete+recreate). */
  assignCourse: (id: string, dto: { roleCodes?: string[]; userIds?: string[]; pointIds?: string[]; dueAt?: string | null }) =>
    api.post<{ ok: true; count: number }>(`/wiki/admin/lms/courses/${encodeURIComponent(id)}/assign`, dto),
  /** Текущие назначения курса. */
  listAssignments: (id: string) =>
    api.get<CourseAssignmentDto>(`/wiki/admin/lms/courses/${encodeURIComponent(id)}/assignments`),
  addModule: (courseId: string, dto: { title: string; articleId?: string | null; quizId?: string | null; videoUrl?: string | null; trainer?: TrainerConfig | null }) =>
    api.post<{ id: string }>(`/wiki/admin/lms/courses/${encodeURIComponent(courseId)}/modules`, dto),
  /** Переименовать урок (модуль). */
  updateModule: (id: string, title: string) =>
    api.patch<{ ok: true }>(`/wiki/admin/lms/modules/${encodeURIComponent(id)}`, { title }),
  /** Переставить уроки курса: ids в нужном порядке → sortOrder 0,10,20… */
  reorderModules: (courseId: string, ids: string[]) =>
    api.patch<{ ok: true }>(`/wiki/admin/lms/courses/${encodeURIComponent(courseId)}/modules/reorder`, { ids }),
  removeModule: (id: string) => api.delete<{ ok: true }>(`/wiki/admin/lms/modules/${encodeURIComponent(id)}`),
  setModuleTrainer: (moduleId: string, trainer: TrainerConfig | null) =>
    api.patch<{ ok: true }>(`/wiki/admin/lms/modules/${encodeURIComponent(moduleId)}/trainer`, { trainer }),
  // ── Уроки (фаза 2.1) ──
  getLesson: (id: string) => api.get<AdminLessonFull>(`/wiki/admin/lms/lessons/${encodeURIComponent(id)}`),
  addLesson: (moduleId: string, dto: { title: string; type?: LessonType }) =>
    api.post<{ id: string }>(`/wiki/admin/lms/modules/${encodeURIComponent(moduleId)}/lessons`, dto),
  updateLesson: (id: string, dto: LessonInput) =>
    api.patch<{ ok: true }>(`/wiki/admin/lms/lessons/${encodeURIComponent(id)}`, dto),
  reorderLessons: (moduleId: string, ids: string[]) =>
    api.patch<{ ok: true }>(`/wiki/admin/lms/modules/${encodeURIComponent(moduleId)}/lessons/reorder`, { ids }),
  removeLesson: (id: string) => api.delete<{ ok: true }>(`/wiki/admin/lms/lessons/${encodeURIComponent(id)}`),
  /**
   * Файл урока (multipart). main=true — видео становится видео урока.
   * onProgress — процент загрузки (XHR; fetch прогресс отдачи не умеет).
   */
  uploadLessonFile: async (id: string, file: File, main = false, onProgress?: (pct: number) => void): Promise<{ id: string; fileName: string; kind: string; url: string }> => {
    const fd = new FormData();
    fd.append('file', file);
    if (onProgress) {
      return xhrUpload<{ id: string; fileName: string; kind: string; url: string }>(
        `/api/wiki/admin/lms/lessons/${encodeURIComponent(id)}/attachments${main ? '?main=1' : ''}`,
        fd,
        onProgress,
      );
    }
    const r = await fetch(`/api/wiki/admin/lms/lessons/${encodeURIComponent(id)}/attachments${main ? '?main=1' : ''}`, {
      method: 'POST',
      body: fd,
      credentials: 'include',
    });
    if (!r.ok) {
      let msg = `Ошибка загрузки (${r.status})`;
      try {
        const j = (await r.json()) as { message?: string | string[] };
        if (j.message) msg = Array.isArray(j.message) ? j.message.join(', ') : j.message;
      } catch { /* не JSON */ }
      throw new Error(msg);
    }
    return (await r.json()) as { id: string; fileName: string; kind: string; url: string };
  },
  createQuiz: (dto: { title: string; passingScore?: number }) => api.post<{ id: string }>('/wiki/admin/lms/quizzes', dto),
  getQuiz: (id: string) => api.get<AdminQuiz>(`/wiki/admin/lms/quizzes/${encodeURIComponent(id)}`),
  updateQuiz: (id: string, dto: QuizPolicyInput) =>
    api.patch<{ ok: true }>(`/wiki/admin/lms/quizzes/${encodeURIComponent(id)}`, dto),
  addQuestion: (quizId: string, dto: QuestionInput) =>
    api.post<{ id: string }>(`/wiki/admin/lms/quizzes/${encodeURIComponent(quizId)}/questions`, dto),
  updateQuestion: (id: string, dto: QuestionInput) =>
    api.patch<{ ok: true }>(`/wiki/admin/lms/questions/${encodeURIComponent(id)}`, dto),
  removeQuestion: (id: string) => api.delete<{ ok: true }>(`/wiki/admin/lms/questions/${encodeURIComponent(id)}`),
  // ── Банки вопросов (фаза 2.2) ──
  listBanks: () => api.get<AdminBank[]>('/wiki/admin/lms/banks'),
  createBank: (dto: { name: string; description?: string | null; tags?: string[] }) => api.post<{ id: string }>('/wiki/admin/lms/banks', dto),
  getBank: (id: string) => api.get<AdminBankFull>(`/wiki/admin/lms/banks/${encodeURIComponent(id)}`),
  updateBank: (id: string, dto: { name?: string; description?: string | null; tags?: string[] }) =>
    api.patch<{ ok: true }>(`/wiki/admin/lms/banks/${encodeURIComponent(id)}`, dto),
  deleteBank: (id: string) => api.delete<{ ok: true }>(`/wiki/admin/lms/banks/${encodeURIComponent(id)}`),
  addBankQuestions: (id: string, questions: QuestionInput[]) =>
    api.post<{ ids: string[] }>(`/wiki/admin/lms/banks/${encodeURIComponent(id)}/questions`, { questions }),
  // ── Траектории (фаза 2.4) ──
  listPaths: () => api.get<AdminPath[]>('/wiki/admin/lms/paths'),
  createPath: (dto: PathInput) => api.post<{ id: string }>('/wiki/admin/lms/paths', dto),
  updatePath: (id: string, dto: PathInput) => api.patch<{ ok: true }>(`/wiki/admin/lms/paths/${encodeURIComponent(id)}`, dto),
  deletePath: (id: string) => api.delete<{ ok: true }>(`/wiki/admin/lms/paths/${encodeURIComponent(id)}`),
  setPathSteps: (id: string, steps: PathStepInput[]) => api.patch<{ ok: true }>(`/wiki/admin/lms/paths/${encodeURIComponent(id)}/steps`, { steps }),
  listPathAssignments: (id: string) => api.get<PathAssignmentRow[]>(`/wiki/admin/lms/paths/${encodeURIComponent(id)}/assignments`),
  assignPath: (id: string, ownerKeys: string[]) => api.post<{ created: number }>(`/wiki/admin/lms/paths/${encodeURIComponent(id)}/assign`, { ownerKeys }),
  unassignPath: (id: string, ownerKey: string) => api.delete<{ ok: true }>(`/wiki/admin/lms/paths/${encodeURIComponent(id)}/assignments/${encodeURIComponent(ownerKey)}`),
  searchPathTargets: (q: string) => api.get<Array<{ ownerKey: string; name: string; sub: string; kind: 'crm' | 'emp' }>>(`/wiki/admin/lms/paths/targets?search=${encodeURIComponent(q)}`),
  runPathAutoAssign: () => api.post<{ paths: number; assigned: number }>('/wiki/admin/lms/paths/auto-assign/run', {}),
  // ── Навыки (фаза 3.1) ──
  listSkills: () => api.get<AdminSkill[]>('/wiki/admin/lms/skills'),
  createSkill: (dto: SkillInput) => api.post<{ id: string }>('/wiki/admin/lms/skills', dto),
  updateSkill: (id: string, dto: SkillInput) => api.patch<{ ok: true }>(`/wiki/admin/lms/skills/${encodeURIComponent(id)}`, dto),
  deleteSkill: (id: string) => api.delete<{ ok: true }>(`/wiki/admin/lms/skills/${encodeURIComponent(id)}`),
  skillsMatrix: (pointId?: string | null) => api.get<SkillsMatrix>(`/wiki/admin/lms/skills/matrix${pointId ? `?pointId=${encodeURIComponent(pointId)}` : ''}`),
  // ── Журнал событий и опросы (фаза 2.6) ──
  eventsCsvUrl: (from: string, to: string) => `/api/wiki/admin/lms/events.csv?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
  eventsSummary: (days = 30) => api.get<Array<{ verb: string; count: number }>>(`/wiki/admin/lms/events/summary?days=${days}`),
  surveyResults: (lessonId: string) =>
    api.get<{ responses: number; questions: Array<{ text: string; kind: string; avg: number | null; texts: string[] }> }>(`/wiki/admin/lms/lessons/${encodeURIComponent(lessonId)}/survey-results`),
  // ── Ручная проверка (фаза 2.3) ──
  listReviews: (status: 'pending' | 'graded' | 'all' = 'pending') =>
    api.get<ReviewQueueItem[]>(`/wiki/admin/lms/reviews?status=${status}`),
  applyReviews: (attemptId: string, items: Array<{ questionId: string; score: number; comment?: string | null }>) =>
    api.post<{ status: string; score: number; passed: boolean; courseCompleted: boolean }>(`/wiki/admin/lms/reviews/${encodeURIComponent(attemptId)}`, { items }),
};

// ── Новости (из News-модуля CRM, shared cookie) ─────────────────────

/**
 * Опрос внутри новости. Полностью соответствует backend `NewsPollDto`
 * (news-polls.service.ts). Вики-вьюер читает его из общего `/news`-эндпоинта
 * (permission `news:view`, shared cookie) и может голосовать через
 * `POST /news/:id/poll/vote` — тот же эндпоинт, что и CRM-партнёр.
 */
export interface NewsPollOptionDto {
  id: string;
  text: string;
  sortOrder: number;
  voteCount: number;
}
export interface NewsPollDto {
  id: string;
  question: string;
  isMultiple: boolean;
  isAnonymous: boolean;
  closedAt: string | null;
  options: NewsPollOptionDto[];
  totalVoters: number;
  /** ID вариантов, за которые проголосовал текущий вьюер. */
  myVoteOptionIds: string[];
}

/** Вложение новости (см. backend NewsAttachment). Скачивается по cookie-auth. */
export interface NewsAttachmentDto {
  id: string;
  /** 'IMAGE' | 'PDF'. */
  type: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  sortOrder: number;
}

export interface NewsItemDto {
  id: string;
  title: string;
  excerpt: string | null;
  bodyHtml: string;
  priority?: string | null;
  isPinned?: boolean;
  publishedAt?: string | null;
  createdAt: string;
  category?: { name: string; color?: string } | null;
  /** Опрос (additive, общий с CRM-лентой). null/undefined — опроса нет. */
  poll?: NewsPollDto | null;
  /** Вложения (additive). Отдаются backend'ом в общем DTO. */
  attachments?: NewsAttachmentDto[];
  /** Per-post флаги каналов рассылки — для бейджей «куда ушла новость». */
  broadcastTelegram?: boolean;
  broadcastMax?: boolean;
}

/** Элемент единой ленты вики (фаза 1.1): CRM-новость или публикация вики. */
export interface WikiFeedItemDto {
  kind: 'news' | 'publication';
  id: string;
  slug: string | null;
  title: string;
  excerpt: string | null;
  coverUrl: string | null;
  icon: string | null;
  categoryName: string | null;
  publishedAt: string | null;
  isRead: boolean;
  isPinned: boolean;
  priority: 'NORMAL' | 'IMPORTANT' | 'REQUIRES_ACTION';
  requiresAck: boolean;
  readingMinutes: number | null;
  /** Полная CRM-новость для модалки (kind=news). */
  news: NewsItemDto | null;
}

export const newsApi = {
  /**
   * Единая лента вики (фаза 1.1): CRM-новости (по ACL CRM или по роли/точке
   * сотрудника iiko) + публикации вики. Работает под WikiViewerGuard —
   * доступна и сотрудникам (раньше /news отдавал им 401).
   */
  feed: (params: { filter?: 'mine' | 'all' | 'required'; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.filter) q.set('filter', params.filter);
    q.set('limit', String(params.limit ?? 50));
    return api.get<{ items: WikiFeedItemDto[]; total: number; readIds: string[] }>(`/wiki/news?${q.toString()}`);
  },
  /** Совместимость: только CRM-новости в старом формате (главная, модалка). */
  list: async (params: { importantOnly?: boolean; limit?: number } = {}) => {
    const r = await newsApi.feed({ limit: params.limit ?? 30 });
    let items = r.items.filter((i) => i.kind === 'news' && i.news).map((i) => i.news as NewsItemDto);
    if (params.importantOnly) items = items.filter((n) => n.priority === 'IMPORTANT' || n.priority === 'REQUIRES_ACTION' || n.isPinned);
    return { items, total: items.length, readIds: r.readIds };
  },
  /** Отметить новость прочитанной (CRM → news_reads, сотрудник → wiki_news_reads). */
  markRead: (newsId: string) => api.post<{ ok: true }>(`/wiki/news/${encodeURIComponent(newsId)}/read`),
  unreadCount: () => api.get<{ count: number }>('/wiki/news/unread-count'),
  /**
   * Проголосовать в опросе новости. Переиспользует СУЩЕСТВУЮЩИЙ
   * CRM-эндпоинт `POST /news/:id/poll/vote` (permission `news:view`,
   * доступен вики-вьюеру по shared cookie). Возвращает свежие результаты
   * с myVoteOptionIds. Пустой массив optionIds — отозвать голос.
   */
  votePoll: (newsId: string, optionIds: string[]) =>
    api.post<NewsPollDto>(`/news/${encodeURIComponent(newsId)}/poll/vote`, { optionIds }),
};

// ── Reads (mark-read + ack) ─────────────────────────────────────────

export const readsApi = {
  markRead: (slug: string, timeSpentSec?: number) =>
    api.post<{ isRead: boolean; readCount: number; isAcked: boolean }>(
      `/wiki/articles/${encodeURIComponent(slug)}/read`,
      { timeSpentSec },
    ),
  ack: (slug: string) =>
    api.post<{ ackedAt: string; ackedVersion: number }>(
      `/wiki/articles/${encodeURIComponent(slug)}/ack`,
      {},
    ),
};

// ── Education (LMS) ──────────────────────────────────────────────────

export interface CourseListItemDto {
  id: string;
  name: string;
  description: string | null;
  forRoles: string[];
  isRequired: boolean;
  sortOrder: number;
  isArchived: boolean;
  level: string | null;
  durationLabel: string | null;
  coverKey: string | null;
  /** Фон плитки #RRGGBB и подпись на ней (null — вид подбирается по id курса). */
  coverColor?: string | null;
  coverText?: string | null;
  hasCertificate: boolean;
  objectives?: string[];
  estimatedMinutes?: number | null;
  isAssigned: boolean;
  dueAt: string | null;
  totalModules: number;
  completedModules: number;
  totalLessons?: number;
  completedLessons?: number;
  progressPercent: number;
  isCompleted: boolean;
}

/** Урок в списке раздела (фаза 2.1). */
export interface LessonSummaryDto {
  id: string;
  type: LessonType;
  title: string;
  estimatedMinutes: number | null;
  hasQuiz: boolean;
  isCompleted: boolean;
}

export interface CourseDetailDto {
  id: string;
  name: string;
  description: string | null;
  forRoles: string[];
  isRequired: boolean;
  level: string | null;
  durationLabel: string | null;
  coverKey: string | null;
  /** Фон плитки #RRGGBB и подпись на ней (null — вид подбирается по id курса). */
  coverColor?: string | null;
  coverText?: string | null;
  hasCertificate: boolean;
  objectives: string[];
  estimatedMinutes: number | null;
  isAssigned: boolean;
  dueAt: string | null;
  totalModules: number;
  completedModules: number;
  totalLessons: number;
  completedLessons: number;
  /** Первый непройденный урок («Продолжить»). */
  continueLessonId: string | null;
  progressPercent: number;
  isCompleted: boolean;
  startedAt: string | null;
  modules: Array<{
    id: string;
    title: string;
    sortOrder: number;
    article: { id: string; slug: string; title: string; excerpt: string | null } | null;
    quiz: { id: string; title: string; passingScore: number } | null;
    hasTrainer: boolean;
    videoUrl?: string | null;
    hasVideo?: boolean;
    lessons: LessonSummaryDto[];
    firstLessonId: string | null;
    nextLessonId: string | null;
    isCompleted: boolean;
    isLocked: boolean;
  }>;
}

/** Данные интерактивного урока-тренажёра (кит «LMS Система»). */
export interface LessonModuleDto {
  moduleId: string;
  /** Урок-тренажёр (фаза 2.1); null у legacy-модуля. */
  lessonId: string | null;
  moduleTitle: string;
  courseId: string;
  courseName: string;
  isCompleted: boolean;
  trainer: TrainerConfig | null;
  toc: Array<{
    id: string;
    title: string;
    done: boolean;
    current: boolean;
    lessons: Array<{ id: string; title: string; type: LessonType; done: boolean; current: boolean }>;
  }>;
}

/** Урок для читателя (фаза 2.1). */
export interface LessonDto {
  id: string;
  type: LessonType;
  title: string;
  objectives: string[];
  estimatedMinutes: number | null;
  bodyHtml: string;
  bodyFromArticle: boolean;
  article: { id: string; slug: string; title: string; excerpt: string | null } | null;
  quiz: { id: string; title: string; passingScore: number; bestScore: number | null; bestPassed: boolean } | null;
  video: {
    attachmentId: string | null;
    url: string | null;
    streamUrl: string | null;
    posterUrl: string | null;
    status: string | null;
    durationSec: number | null;
    fileName: string | null;
  } | null;
  videoProgress: { positionSec: number; completed: boolean } | null;
  trainer: TrainerConfig | null;
  /** Опрос (фаза 2.6). */
  survey: { questions: Array<{ text: string; kind: 'scale' | 'text'; scale?: number }>; answered: boolean } | null;
  externalUrl: string | null;
  /** SCORM-курс урока (type=scorm): текущая версия пакета. */
  scorm: {
    packageId: string;
    status: ScormPackageStatus;
    error: string | null;
    title: string | null;
    version: '1.2' | '2004' | null;
    launchPath: string | null;
    masteryScore: number | null;
  } | null;
  isCompleted: boolean;
  /** Прогресс пишется (CRM-юзер и сотрудник iiko); false — только у анонима. */
  canTrackProgress: boolean;
  moduleId: string;
  moduleTitle: string;
  courseId: string;
  courseName: string;
  totalLessons: number;
  completedLessons: number;
  progressPercent: number;
  prev: { id: string; title: string } | null;
  next: { id: string; title: string } | null;
  toc: Array<{
    id: string;
    title: string;
    done: boolean;
    current: boolean;
    lessons: Array<{ id: string; title: string; type: LessonType; estimatedMinutes: number | null; done: boolean; current: boolean }>;
  }>;
}

/** Вопрос попытки (фаза 2.2): без правильных ответов. */
export interface QuizClientQuestion {
  id: string;
  kind: QuestionKind;
  text: string;
  points: number;
  options: Array<{ id: string; text: string }>;
  /** matching: {left, right}; ordering: {items}; hotspot: {imageUrl}; open/photo: {hint}. */
  payload: Record<string, unknown> | null;
}

export interface QuizForAttemptDto {
  id: string;
  title: string;
  passingScore: number;
  questions: QuizClientQuestion[];
  totalQuestions: number;
  policy: { maxAttempts: number | null; cooldownMinutes: number; timeLimitMinutes: number | null; showCorrectAnswers: boolean };
  attemptsUsed: number;
  /** null — без лимита. */
  attemptsRemaining: number | null;
  nextAttemptAt: string | null;
  openAttempt: { id: string; startedAt: string; deadlineAt: string | null } | null;
  bestScore: number | null;
  bestPassed: boolean;
  pendingReview: boolean;
}

export interface QuizAttemptQuestionsDto {
  attemptId: string;
  quizId: string;
  title: string;
  passingScore: number;
  timeLimitMinutes: number | null;
  startedAt: string;
  deadlineAt: string | null;
  questions: QuizClientQuestion[];
}

export interface QuizAnswerInput {
  questionId: string;
  selectedOptionIds?: string[];
  value?: unknown;
}

export interface QuizSubmitResult {
  score: number;
  passed: boolean;
  pendingReview: boolean;
  attemptsRemaining: number | null;
  nextAttemptAt: string | null;
  correctCount: number;
  totalCount: number;
  courseCompleted?: boolean;
  results: Array<{ questionId: string; correct: boolean | null; explanation: string | null; correctOptionIds: string[] | null }>;
}

/** Выданный сертификат о прохождении курса. */
export interface CertificateDto {
  number: string;
  issuedAt: string;
  /** Фаза 3.2: срок действия; null — бессрочно. */
  expiresAt?: string | null;
  isExpired?: boolean;
  courseName: string;
  userName: string;
}

/** Геймификация (фаза 3.6): виджет «Мои очки». */
export interface MyGamificationDto {
  points: number;
  streakDays: number;
  badges: Array<{ code: string; name: string; icon: string; description: string; earned: boolean; at: string | null }>;
  leaderboardOptIn: boolean;
  scope: string | null;
  leaderboard: Array<{ rank: number; name: string; points: number; isMe: boolean }>;
  recent: Array<{ delta: number; reason: string; at: string }>;
}
export interface PointsRatingRow {
  tradingPointId: string;
  pointName: string;
  city: string | null;
  members: number;
  active: number;
  totalPoints: number;
  avgPoints: number;
  badges: number;
}

export const educationApi = {
  gamificationMine: () => api.get<MyGamificationDto | null>('/wiki/education/gamification/mine'),
  gamificationPrefs: (leaderboardOptIn: boolean) =>
    api.patch<{ leaderboardOptIn: boolean }>('/wiki/education/gamification/prefs', { leaderboardOptIn }),
  listCourses: () => api.get<CourseListItemDto[]>('/wiki/education/courses'),
  getCourse: (id: string) =>
    api.get<CourseDetailDto>(`/wiki/education/courses/${encodeURIComponent(id)}`),
  /** Метаданные выданного сертификата (404/409 если ещё не получен). */
  getCertificate: (id: string) =>
    api.get<CertificateDto>(`/wiki/education/courses/${encodeURIComponent(id)}/certificate`),
  /** URL PDF-бланка сертификата (window.open, cookie same-origin). */
  certificatePdfUrl: (id: string) =>
    `/api/wiki/education/courses/${encodeURIComponent(id)}/certificate.pdf`,
  markModuleRead: (moduleId: string) =>
    api.post<{ ok: true; courseCompleted: boolean }>(
      `/wiki/education/modules/${encodeURIComponent(moduleId)}/mark-read`,
      {},
    ),
  /** Интерактивный урок-тренажёр (кит «LMS»): конфиг + TOC + контекст курса. Принимает id урока или модуля. */
  getLesson: (moduleId: string) =>
    api.get<LessonModuleDto>(`/wiki/education/modules/${encodeURIComponent(moduleId)}/lesson`),
  /** Мои траектории (фаза 2.4). */
  myPaths: () => api.get<MyPathDto[]>('/wiki/education/paths/mine'),
  /** Вопрос дня (фаза 3.3). */
  reviewToday: () => api.get<{ question: QuizClientQuestion | null; answered: boolean; correct: boolean | null; dueCount: number; streakDays: number }>('/wiki/education/review/today'),
  reviewAnswer: (questionId: string, selectedOptionIds: string[]) =>
    api.post<{ correct: boolean; explanation: string | null; correctOptionIds: string[]; nextInDays: number }>('/wiki/education/review/answer', { questionId, selectedOptionIds }),
  /** Навыки и наставник (фаза 3.1). */
  mySkills: () => api.get<MySkill[]>('/wiki/education/skills/mine'),
  mentorPoints: () => api.get<Array<{ id: string; label: string }>>('/wiki/education/mentor/points'),
  mentorTeam: (pointId?: string | null) => api.get<MentorTeam>(`/wiki/education/mentor/team${pointId ? `?pointId=${encodeURIComponent(pointId)}` : ''}`),
  mentorChecklist: (skillId: string) => api.get<{ skillId: string; skillName: string; articleTitle: string | null; articleSlug: string | null; items: Array<{ key: string; label: string }> }>(`/wiki/education/mentor/checklist/${encodeURIComponent(skillId)}`),
  mentorCheck: (dto: { skillId: string; ownerKey: string; items: Array<{ key: string; ok: boolean; photoAttachmentId?: string | null }>; comment?: string | null }) =>
    api.post<{ passed: boolean; level: number }>('/wiki/education/mentor/checks', dto),
  /** Опрос после курса (фаза 2.6): ответы по порядку вопросов. */
  submitSurvey: (lessonId: string, answers: Array<number | string | null>) =>
    api.post<{ ok: true; courseCompleted: boolean }>(`/wiki/education/lessons/${encodeURIComponent(lessonId)}/survey`, { answers }),
  /** Урок (фаза 2.1). Принимает и id модуля — вернёт его первый урок. */
  getLessonById: (id: string) => api.get<LessonDto>(`/wiki/education/lessons/${encodeURIComponent(id)}`),
  /** result — итог тренажёра (промахи, режим) для журнала событий; необязательно. */
  completeLesson: (id: string, result?: { misses?: number; mode?: 'steps' | 'video' }) =>
    api.post<{ ok: true; courseCompleted: boolean }>(`/wiki/education/lessons/${encodeURIComponent(id)}/complete`, result ?? {}),
  videoProgress: (id: string, positionSec: number, durationSec: number | null) =>
    api.post<{ ok: true; completed: boolean; courseCompleted: boolean }>(
      `/wiki/education/lessons/${encodeURIComponent(id)}/video-progress`,
      { positionSec: Math.max(0, Math.floor(positionSec)), durationSec: durationSec && durationSec > 0 ? Math.floor(durationSec) : undefined },
    ),
  getQuiz: (id: string) =>
    api.get<QuizForAttemptDto>(`/wiki/education/quizzes/${encodeURIComponent(id)}`),
  /** Начать попытку (фаза 2.2): выпавшие вопросы + дедлайн. */
  startQuiz: (id: string) => api.post<QuizAttemptQuestionsDto>(`/wiki/education/quizzes/${encodeURIComponent(id)}/start`, {}),
  /** Фото-ответ (фаза 2.3): картинка во вложения попытки. */
  uploadAttemptPhoto: async (attemptId: string, file: File): Promise<{ id: string; url: string; fileName: string }> => {
    // Сервер режет файл ученика на 25 МБ ещё в потоке (аудит 31.10.2026, R33) — понятный отказ до отправки.
    const tooBig = learnerPhotoSizeError(file);
    if (tooBig) throw new Error(tooBig);
    const fd = new FormData();
    fd.append('file', file);
    const r = await fetch(`/api/wiki/education/attempts/${encodeURIComponent(attemptId)}/photo`, { method: 'POST', body: fd, credentials: 'include' });
    if (!r.ok) {
      let serverMessage: string | string[] | undefined;
      try {
        serverMessage = ((await r.json()) as { message?: string | string[] }).message;
      } catch { /* не JSON */ }
      throw new Error(attemptPhotoErrorText(r.status, serverMessage));
    }
    return (await r.json()) as { id: string; url: string; fileName: string };
  },
  submitQuiz: (id: string, answers: QuizAnswerInput[], attemptId?: string) =>
    api.post<QuizSubmitResult>(
      `/wiki/education/quizzes/${encodeURIComponent(id)}/attempt`,
      { attemptId, answers },
    ),
  getTeamProgress: (courseId: string) =>
    api.get<
      Array<{
        /** crm:<userId> | emp:<employeeAccountId> */
        ownerKey: string;
        /** users.id у CRM-юзера; null у сотрудника iiko. */
        userId: string | null;
        kind: 'crm' | 'employee';
        fullName: string;
        roleName: string;
        progressPercent: number;
        completedAt: string | null;
        bestScore: number | null;
      }>
    >(`/wiki/education/courses/${encodeURIComponent(courseId)}/team-progress`),
};
